import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import SignatureEditor from './SignatureEditor';
import RichBodyEditor from './RichBodyEditor';
import { 
  Users, 
  Mail, 
  Send, 
  Target,
  BarChart3, 
  Search, 
  Plus,
  Upload,
  Trash2, 
  CheckCircle, 
  Check,
  X,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Play,
  Pause,
  RefreshCw,
  FileText,
  Sparkles,
  Shield,
  Share2,
  Link,
  TrendingUp,  Eye,
  Edit,
  MousePointer,
  MessageSquare,
  Ban,
  LogOut,
  Settings,
  ChevronRight,
  ChevronLeft,
  Filter,
  Zap,
  Bot,
  Calendar,
  Clock,
  Globe,
  Building2,
  MapPin,
  List as ListIcon,
  Inbox,
  Pencil
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Reach embed shell — used when a legacy outreach page is opened from inside Reach (?from=reach).
// Renders a Paify Reach header + minimal nav so the user always knows where they are and can return.
const ReachEmbedNav = ({ active }) => {
  const navigate = useNavigate();
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');
  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };
  const Tab = ({ to, label, key: k }) => (
    <button
      onClick={() => navigate(to)}
      className={`px-4 py-3 whitespace-nowrap border-b-2 transition-colors ${
        active === k ? 'text-white border-[#e6007e]' : 'text-gray-400 hover:text-white border-transparent'
      }`}
    >
      {label}
    </button>
  );
  return (
    <>
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Reach</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 rounded border border-gray-700"
            >
              ← Back to Paify Admin
            </button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 flex gap-2 overflow-x-auto">
          <Tab to="/admin/reach" label="Overview" />
          <Tab to="/admin/reach/folders" label="Folders" />
          <Tab to="/admin/reach/templates" label="Templates" k="templates" />
          <Tab to="/admin/reach/smtp" label="SMTP" k="settings" />
          <Tab to="/admin/reach/referrals" label="Referrals" k="referrals" />
          <Tab to="/admin/reach/analytics" label="Analytics" />
          <Tab to="/admin/reach/settings" label="Settings" />
        </div>
      </nav>
    </>
  );
};

// ==================== OUTREACH DASHBOARD ====================

export const OutreachDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendWindow, setSendWindow] = useState(null);
  const [warmup, setWarmup] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchAnalytics();
    fetchSendWindow();
    fetchWarmup();
    const interval = setInterval(() => { fetchSendWindow(); fetchWarmup(); }, 60000);
    return () => clearInterval(interval);
  }, [adminToken, navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch outreach analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSendWindow = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/send-window/status`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSendWindow(r.data);
    } catch {
      // non-critical; ignore
    }
  };

  const fetchWarmup = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/warmup/status`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setWarmup(r.data);
    } catch {
      // non-critical; ignore
    }
  };

  const handleResetWarmup = async () => {
    if (!window.confirm('Restart the 14-day warm-up from day 1? Use this when switching SMTP provider.')) return;
    try {
      await axios.post(`${API}/api/admin/outreach/warmup/reset`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: 'Warm-up reset', description: 'Day 1 begins now.' });
      fetchWarmup();
    } catch (err) {
      toast({ title: 'Reset failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button 
              onClick={() => navigate('/admin/outreach')}
              className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Overview
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/leads')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/lists')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
              data-testid="overview-lists-nav"
            >
              <ListIcon className="w-4 h-4 inline mr-2" />
              Lists
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/campaigns')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <Send className="w-4 h-4 inline mr-2" />
              Campaigns
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/quick-send')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Quick Send
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/templates')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Templates
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/sector-presets')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
              data-testid="sector-presets-nav"
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Sector Presets
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/engine-settings')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
              data-testid="engine-settings-nav"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Engine Settings
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/settings')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
              data-testid="smtp-settings-nav"
            >
              <Mail className="w-4 h-4 inline mr-2" />
              SMTP & DNS
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/referrals')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <Share2 className="w-4 h-4 inline mr-2" />
              Referrals
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/pipeline')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Pipeline
            </button>
            <button 
              onClick={() => navigate('/admin/outreach/settings')}
              className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap"
            >
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Outreach Analytics</h1>
            <p className="text-gray-400 mb-2">Monitor your acquisition engine performance</p>
          </div>
          {sendWindow && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-3 max-w-md ${
                sendWindow.in_window
                  ? 'border-green-500/40 bg-green-500/10'
                  : 'border-yellow-500/40 bg-yellow-500/10'
              }`}
              data-testid="send-window-badge"
            >
              <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${sendWindow.in_window ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'}`}></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white">
                  {sendWindow.in_window ? 'Send window OPEN — emails firing' : 'Send window CLOSED — emails paused'}
                </p>
                <p className="text-xs text-gray-300 mt-0.5">
                  Mon-Fri {sendWindow.send_hours} {sendWindow.timezone.split('/')[1].replace('_',' ')}
                  {sendWindow.skip_holidays && <span> · skips UK bank holidays</span>}
                </p>
                {!sendWindow.in_window && sendWindow.next_open_iso && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Next open: {new Date(sendWindow.next_open_iso).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            </div>
          )}
          {warmup && warmup.active && !warmup.completed && (
            <div
              className="rounded-lg border px-4 py-3 text-sm flex items-start gap-3 max-w-md border-blue-500/40 bg-blue-500/10"
              data-testid="warmup-badge"
            >
              <div className="w-2.5 h-2.5 rounded-full mt-1.5 bg-blue-400 animate-pulse"></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white">
                  SMTP warm-up · Day {warmup.current_day} of {warmup.total_days}
                </p>
                <p className="text-xs text-gray-300 mt-0.5">
                  Today's cap: <strong>{warmup.effective_limit}</strong> · sent: {warmup.sent_today} · remaining: {warmup.remaining_today}
                  {warmup.next_day_cap && <span> · tomorrow: {warmup.next_day_cap}</span>}
                </p>
                <button
                  type="button"
                  onClick={handleResetWarmup}
                  className="text-xs text-blue-300 hover:text-blue-200 underline mt-1"
                  data-testid="warmup-reset-btn"
                >
                  Reset to day 1
                </button>
              </div>
            </div>
          )}
          {warmup && warmup.completed && (
            <div
              className="rounded-lg border px-4 py-3 text-sm flex items-start gap-3 max-w-md border-emerald-500/40 bg-emerald-500/10"
              data-testid="warmup-complete-badge"
            >
              <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5" />
              <div>
                <p className="font-semibold text-white">SMTP warm-up complete</p>
                <p className="text-xs text-gray-300">Sending at full cap of {warmup.configured_limit}/day · {warmup.sent_today} sent today</p>
              </div>
            </div>
          )}
        </div>

        {analytics && (
          <>
            {/* Lead Stats */}
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-[#e6007e]" />
              Lead Pipeline
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Total Leads</p>
                  <p className="text-2xl font-bold text-white">{analytics.leads?.total || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Verified</p>
                  <p className="text-2xl font-bold text-green-400">{analytics.leads?.verified || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Contacted</p>
                  <p className="text-2xl font-bold text-blue-400">{analytics.leads?.contacted || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Replied</p>
                  <p className="text-2xl font-bold text-purple-400">{analytics.leads?.replied || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Converted</p>
                  <p className="text-2xl font-bold text-[#e6007e]">{analytics.leads?.converted || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Email Performance */}
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-[#e6007e]" />
              Email Performance
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Sent</p>
                  <p className="text-xl font-bold text-white">{analytics.emails?.sent || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Open Rate</p>
                  <p className="text-xl font-bold text-green-400">{analytics.rates?.open_rate || 0}%</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Click Rate</p>
                  <p className="text-xl font-bold text-blue-400">{analytics.rates?.click_rate || 0}%</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Reply Rate</p>
                  <p className="text-xl font-bold text-purple-400">{analytics.rates?.reply_rate || 0}%</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Bounce Rate</p>
                  <p className="text-xl font-bold text-orange-400">{analytics.rates?.bounce_rate || 0}%</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-4 pb-4">
                  <p className="text-gray-400 text-xs">Unsubscribe</p>
                  <p className="text-xl font-bold text-red-400">{analytics.rates?.unsubscribe_rate || 0}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gray-800 border-gray-700 hover:border-[#e6007e]/50 cursor-pointer transition-colors" onClick={() => navigate('/admin/outreach/leads')}>
                <CardContent className="pt-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">Import Leads</h3>
                    <p className="text-gray-400 text-sm">Add new leads via CSV or one-by-one</p>
                  </div>
                  <Upload className="w-8 h-8 text-[#e6007e]" />
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700 hover:border-[#e6007e]/50 cursor-pointer transition-colors" onClick={() => navigate('/admin/outreach/lists')} data-testid="overview-quickaction-lists">
                <CardContent className="pt-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">Lists & Send</h3>
                    <p className="text-gray-400 text-sm">Pick a list (e.g. Plumbers) → Send</p>
                  </div>
                  <ListIcon className="w-8 h-8 text-[#e6007e]" />
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700 hover:border-[#e6007e]/50 cursor-pointer transition-colors" onClick={() => navigate('/admin/outreach/templates')}>
                <CardContent className="pt-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">AI Generate Email</h3>
                    <p className="text-gray-400 text-sm">Create personalized templates with AI</p>
                  </div>
                  <Sparkles className="w-8 h-8 text-[#e6007e]" />
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
};


// ==================== LEADS PAGE ====================

export const OutreachLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHunterModal, setShowHunterModal] = useState(false);
  const [newLead, setNewLead] = useState({ email: '', first_name: '', last_name: '', company_name: '', industry: '', campaign_id: '' });
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [bulkCampaignId, setBulkCampaignId] = useState('');
  const [hunterDomain, setHunterDomain] = useState('');
  const [importData, setImportData] = useState('');
  const [importMode, setImportMode] = useState('file'); // 'file' or 'paste'
  const [importFile, setImportFile] = useState(null);
  const [importDragActive, setImportDragActive] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [enrichLead, setEnrichLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchLeads();
    fetchAvailableCampaigns();
  }, [adminToken, navigate, searchQuery, statusFilter]);

  const fetchAvailableCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/campaigns`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setAvailableCampaigns(r.data.campaigns || []);
    } catch {
      // non-critical
    }
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/api/admin/outreach/leads?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setLeads(response.data.leads || []);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch leads",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    try {
      const { campaign_id, ...leadFields } = newLead;
      await axios.post(`${API}/api/admin/outreach/leads`, leadFields, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      let extra = '';
      if (campaign_id) {
        try {
          const r = await axios.post(
            `${API}/api/admin/outreach/campaigns/${campaign_id}/quick-add`,
            { email: leadFields.email, full_name: `${leadFields.first_name || ''} ${leadFields.last_name || ''}`.trim(), company_name: leadFields.company_name },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          if (r.data?.enrolled) extra = ' + enrolled in campaign';
        } catch (_e) {
          // soft-fail
        }
      }
      toast({ title: 'Lead added' + extra });
      setShowAddModal(false);
      setNewLead({ email: '', first_name: '', last_name: '', company_name: '', industry: '', campaign_id: '' });
      fetchLeads();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to add lead",
        variant: "destructive"
      });
    }
  };

  const handleAddOneLeadToCampaign = async (lead, campaignId) => {
    if (!campaignId) return;
    try {
      const r = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/quick-add`,
        { email: lead.email, full_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(), company_name: lead.company_name },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: r.data.enrolled ? 'Added to campaign' : 'Already in this campaign', description: r.data.message });
    } catch (err) {
      toast({ title: 'Could not add', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleBulkAddToCampaign = async () => {
    if (!bulkCampaignId || selectedLeadIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedLeadIds);
      const leadDocs = leads.filter((l) => selectedLeadIds.has(l.id));
      let enrolled = 0;
      for (const l of leadDocs) {
        try {
          const r = await axios.post(
            `${API}/api/admin/outreach/campaigns/${bulkCampaignId}/quick-add`,
            { email: l.email, full_name: `${l.first_name || ''} ${l.last_name || ''}`.trim(), company_name: l.company_name },
            { headers: { Authorization: `Bearer ${adminToken}` } }
          );
          if (r.data?.enrolled) enrolled += 1;
        } catch (_e) {
          // continue
        }
      }
      toast({ title: 'Added to campaign', description: `${enrolled} of ${ids.length} enrolled (others were already in).` });
      setSelectedLeadIds(new Set());
      setBulkCampaignId('');
    } catch (err) {
      toast({ title: 'Bulk add failed', description: err.message, variant: 'destructive' });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleVerifyLead = async (leadId) => {
    try {
      await axios.post(`${API}/api/admin/outreach/leads/${leadId}/verify`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Email verified" });
      fetchLeads();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to verify email",
        variant: "destructive"
      });
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/outreach/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Lead deleted" });
      fetchLeads();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive"
      });
    }
  };

  const handleImportLeads = async () => {
    setImportLoading(true);
    setImportResult(null);
    try {
      if (importMode === 'file') {
        if (!importFile) {
          toast({ title: "No file selected", description: "Drop a CSV file or click to choose one.", variant: "destructive" });
          setImportLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', importFile);
        const response = await axios.post(`${API}/api/admin/outreach/leads/import-csv`, formData, {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        setImportResult(response.data);
        toast({
          title: "Import complete",
          description: `Imported ${response.data.imported}, skipped ${response.data.skipped_duplicate} duplicates.`
        });
      } else {
        // Manual paste mode (legacy)
        const lines = importData.trim().split('\n');
        const leads = lines.map(line => {
          const [email, first_name, last_name, company_name] = line.split(',').map(s => s.trim());
          return { email, first_name, last_name, company_name };
        }).filter(l => l.email);

        const response = await axios.post(`${API}/api/admin/outreach/leads/import`, {
          leads,
          source: 'csv_paste',
          auto_verify: false,
        }, { headers: { Authorization: `Bearer ${adminToken}` } });
        setImportResult({
          imported: response.data.imported || leads.length,
          skipped_duplicate: response.data.skipped || 0,
          errors: response.data.errors || [],
        });
        toast({ title: "Imported", description: `${leads.length} leads queued.` });
      }
      fetchLeads();
    } catch (err) {
      toast({
        title: "Import failed",
        description: err.response?.data?.detail || "Could not import leads.",
        variant: "destructive",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleBulkAction = async (action, status = null) => {
    if (selectedLeadIds.size === 0) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${selectedLeadIds.size} lead(s)?`)) return;

    setBulkLoading(true);
    try {
      const body = {
        action,
        lead_ids: Array.from(selectedLeadIds),
      };
      if (action === 'add_tags') {
        body.tags = bulkTagInput.split(',').map((t) => t.trim()).filter(Boolean);
      }
      if (action === 'set_status') body.status = status;

      const res = await axios.post(`${API}/api/admin/outreach/leads/bulk-action`, body, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast({
        title: 'Done',
        description: `${action.replace('_', ' ')}: ${res.data.affected || 0} lead(s) updated.`,
      });
      if (action === 'add_tags') setBulkTagInput('');
      setSelectedLeadIds(new Set());
      fetchLeads();
    } catch (err) {
      toast({
        title: 'Bulk action failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportData('');
    setImportResult(null);
    setImportDragActive(false);
    setImportMode('file');
  };

  const handleDragOver = (e) => { e.preventDefault(); setImportDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setImportDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setImportDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.toLowerCase().endsWith('.csv')) {
      setImportFile(f);
    } else {
      toast({ title: "Invalid file", description: "Please drop a .csv file.", variant: "destructive" });
    }
  };

  const handleHunterSearch = async () => {
    try {
      const response = await axios.post(`${API}/api/admin/outreach/prospect/domain-search`, {
        domain: hunterDomain
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      toast({ 
        title: "Success", 
        description: `Found ${response.data.emails_found} emails, created ${response.data.leads_created} new leads` 
      });
      setShowHunterModal(false);
      setHunterDomain('');
      fetchLeads();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to search domain",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: 'bg-gray-500',
      verified: 'bg-green-500',
      invalid: 'bg-red-500',
      contacted: 'bg-blue-500',
      replied: 'bg-purple-500',
      converted: 'bg-[#e6007e]',
      unsubscribed: 'bg-orange-500',
      bounced: 'bg-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs text-white ${colors[status] || 'bg-gray-500'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Send className="w-4 h-4 inline mr-2" />Campaigns
            </button>
            <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <FileText className="w-4 h-4 inline mr-2" />Templates
            </button>
            <button onClick={() => navigate('/admin/outreach/referrals')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Share2 className="w-4 h-4 inline mr-2" />Referrals
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Lead Database</h1>
            <p className="text-gray-400">{leads.length} total leads</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => setShowImportModal(true)} className="border-gray-600 text-gray-300">
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="bg-[#e6007e] hover:bg-[#c4006b]">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search by email, name, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leads Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-0">
            {selectedLeadIds.size > 0 && (
              <div className="flex flex-wrap items-center gap-3 p-3 border-b border-gray-700 bg-gray-900/60" data-testid="bulk-action-toolbar">
                <span className="text-sm text-gray-300 font-semibold">
                  {selectedLeadIds.size} selected
                </span>
                <Input
                  placeholder="Tag name (e.g. plumbers)"
                  value={bulkTagInput}
                  onChange={(e) => setBulkTagInput(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white text-sm h-9 w-48"
                  data-testid="bulk-tag-input"
                />
                <Button
                  size="sm"
                  onClick={() => handleBulkAction('add_tags')}
                  disabled={bulkLoading || !bulkTagInput.trim()}
                  className="bg-[#e6007e] hover:bg-[#c4006b] disabled:opacity-50"
                  data-testid="bulk-add-tag-btn"
                >
                  Add tag
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleBulkAction('set_status', 'verified')}
                  disabled={bulkLoading}
                  className="text-green-400 hover:text-green-300"
                  data-testid="bulk-mark-verified-btn"
                >
                  Mark verified
                </Button>
                <select
                  value={bulkCampaignId}
                  onChange={(e) => setBulkCampaignId(e.target.value)}
                  className="bg-gray-800 border border-gray-600 text-white text-sm rounded-md px-2 h-9"
                  data-testid="bulk-campaign-select"
                >
                  <option value="">Add to campaign…</option>
                  {availableCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={handleBulkAddToCampaign}
                  disabled={bulkLoading || !bulkCampaignId}
                  className="bg-[#e6007e] hover:bg-[#c4006b] disabled:opacity-50"
                  data-testid="bulk-add-to-campaign-btn"
                >
                  Send to campaign
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleBulkAction('delete')}
                  disabled={bulkLoading}
                  className="text-red-400 hover:text-red-300"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedLeadIds(new Set())}
                  className="ml-auto text-gray-400"
                  data-testid="bulk-clear-btn"
                >
                  Clear selection
                </Button>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="p-4 w-10">
                    <input
                      type="checkbox"
                      checked={leads.length > 0 && selectedLeadIds.size === leads.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeadIds(new Set(leads.map((l) => l.id)));
                        } else {
                          setSelectedLeadIds(new Set());
                        }
                      }}
                      className="cursor-pointer"
                      data-testid="select-all-leads-checkbox"
                    />
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">Email</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Name</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Company</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Tags</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Score</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className={`border-b border-gray-700/50 hover:bg-gray-700/30 ${selectedLeadIds.has(lead.id) ? 'bg-pink-500/5' : ''}`}>
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedLeadIds.has(lead.id)}
                        onChange={(e) => {
                          const next = new Set(selectedLeadIds);
                          if (e.target.checked) next.add(lead.id); else next.delete(lead.id);
                          setSelectedLeadIds(next);
                        }}
                        className="cursor-pointer"
                        data-testid={`lead-checkbox-${lead.id}`}
                      />
                    </td>
                    <td className="p-4 text-white">{lead.email}</td>
                    <td className="p-4 text-gray-300">{lead.first_name} {lead.last_name}</td>
                    <td className="p-4 text-gray-300">{lead.company_name || '-'}</td>
                    <td className="p-4">
                      {lead.tags && lead.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {lead.tags.slice(0, 3).map((t) => (
                            <span key={t} className="text-xs bg-gray-700 text-gray-200 px-2 py-0.5 rounded">{t}</span>
                          ))}
                          {lead.tags.length > 3 && (
                            <span className="text-xs text-gray-400">+{lead.tags.length - 3}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-4">{getStatusBadge(lead.status)}</td>
                    <td className="p-4 text-gray-300">{lead.email_verification_score || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {availableCampaigns.length > 0 && (
                          <select
                            onChange={(e) => { if (e.target.value) { handleAddOneLeadToCampaign(lead, e.target.value); e.target.value = ''; } }}
                            className="bg-gray-700 border border-gray-600 text-gray-200 text-xs rounded px-2 py-1 h-7"
                            data-testid={`row-add-to-campaign-${lead.id}`}
                            defaultValue=""
                          >
                            <option value="">+ Campaign</option>
                            {availableCampaigns.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setEnrichLead(lead)} title="View lead details" data-testid={`view-lead-${lead.id}`}>
                          <Eye className="w-4 h-4 text-blue-400" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleVerifyLead(lead.id)} title="Verify email">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteLead(lead.id)} title="Delete lead">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-gray-400">
                      No leads found. Import some leads to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      {/* Add Lead Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-400">Email *</Label>
              <Input
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                className="bg-gray-700 border-gray-600"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">First Name</Label>
                <Input
                  value={newLead.first_name}
                  onChange={(e) => setNewLead({...newLead, first_name: e.target.value})}
                  className="bg-gray-700 border-gray-600"
                />
              </div>
              <div>
                <Label className="text-gray-400">Last Name</Label>
                <Input
                  value={newLead.last_name}
                  onChange={(e) => setNewLead({...newLead, last_name: e.target.value})}
                  className="bg-gray-700 border-gray-600"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-400">Company</Label>
              <Input
                value={newLead.company_name}
                onChange={(e) => setNewLead({...newLead, company_name: e.target.value})}
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div>
              <Label className="text-gray-400">Industry</Label>
              <Input
                value={newLead.industry}
                onChange={(e) => setNewLead({...newLead, industry: e.target.value})}
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div>
              <Label className="text-gray-400">Send to campaign <span className="text-xs text-gray-500">(optional)</span></Label>
              <select
                value={newLead.campaign_id}
                onChange={(e) => setNewLead({...newLead, campaign_id: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 mt-1 text-white"
                data-testid="add-lead-campaign-select"
              >
                <option value="">— Don't add to a campaign —</option>
                {availableCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Add the lead and enroll them in one click.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddLead} className="bg-[#e6007e] hover:bg-[#c4006b]">Add Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Enrichment Panel */}
      <Dialog open={!!enrichLead} onOpenChange={(o) => !o && setEnrichLead(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-xl" data-testid="enrich-lead-dialog">
          {enrichLead && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#e6007e] to-[#9b1d63] flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                    {(enrichLead.first_name || enrichLead.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="truncate">
                      {[enrichLead.first_name, enrichLead.last_name].filter(Boolean).join(' ') || enrichLead.email}
                    </DialogTitle>
                    <DialogDescription className="text-gray-400 truncate">
                      {enrichLead.notes?.replace(/^Job title: /, '') || enrichLead.company_name || enrichLead.email}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {/* Contact actions */}
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`mailto:${enrichLead.email}`}
                    className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm transition"
                    data-testid="lead-action-email"
                  >
                    <Mail className="w-4 h-4" /> Email
                  </a>
                  {enrichLead.phone ? (
                    <a
                      href={`tel:${enrichLead.phone.replace(/\s+/g, '')}`}
                      className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm transition"
                      data-testid="lead-action-phone"
                    >
                      <MessageSquare className="w-4 h-4" /> Call {enrichLead.phone}
                    </a>
                  ) : (
                    <div className="flex items-center justify-center gap-2 bg-gray-800 text-gray-500 px-3 py-2 rounded-md text-sm border border-gray-700">
                      <MessageSquare className="w-4 h-4" /> No phone
                    </div>
                  )}
                  {enrichLead.linkedin_url ? (
                    <a
                      href={enrichLead.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-[#0a66c2] hover:bg-[#084d92] text-white px-3 py-2 rounded-md text-sm transition"
                      data-testid="lead-action-linkedin"
                    >
                      <Link className="w-4 h-4" /> Open LinkedIn
                    </a>
                  ) : (
                    <div className="flex items-center justify-center gap-2 bg-gray-800 text-gray-500 px-3 py-2 rounded-md text-sm border border-gray-700">
                      <Link className="w-4 h-4" /> No LinkedIn
                    </div>
                  )}
                  {enrichLead.website ? (
                    <a
                      href={enrichLead.website.startsWith('http') ? enrichLead.website : `https://${enrichLead.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm transition"
                      data-testid="lead-action-website"
                    >
                      <Globe className="w-4 h-4" /> {enrichLead.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  ) : (
                    <div className="flex items-center justify-center gap-2 bg-gray-800 text-gray-500 px-3 py-2 rounded-md text-sm border border-gray-700">
                      <Globe className="w-4 h-4" /> No website
                    </div>
                  )}
                </div>

                {/* Lead facts */}
                <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Email</span>
                    <span className="text-white truncate ml-2">{enrichLead.email}</span>
                  </div>
                  {enrichLead.company_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Company</span>
                      <span className="text-white truncate ml-2">{enrichLead.company_name}</span>
                    </div>
                  )}
                  {enrichLead.industry && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Industry</span>
                      <span className="text-white truncate ml-2">{enrichLead.industry}</span>
                    </div>
                  )}
                  {enrichLead.company_size && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Size</span>
                      <span className="text-white">{enrichLead.company_size}</span>
                    </div>
                  )}
                  {enrichLead.location && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Location</span>
                      <span className="text-white">{enrichLead.location}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Verification</span>
                    <span className="flex items-center gap-2">
                      {enrichLead.email_verified ? (
                        <span className="text-green-400 font-medium">Valid</span>
                      ) : (
                        <span className="text-yellow-400 font-medium">{enrichLead.email_verification_status || 'unverified'}</span>
                      )}
                      {enrichLead.email_verification_score != null && (
                        <span className="text-xs bg-gray-700 px-2 py-0.5 rounded">{enrichLead.email_verification_score}/100</span>
                      )}
                    </span>
                  </div>
                  {enrichLead.source && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Source</span>
                      <span className="text-white">{enrichLead.source}</span>
                    </div>
                  )}
                </div>

                {/* External research tools */}
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Research</p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(`${enrichLead.first_name || ''} ${enrichLead.last_name || ''} ${enrichLead.company_name || ''}`.trim())}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 px-3 py-1.5 rounded-md transition"
                      data-testid="lead-research-google"
                    >
                      Google search
                    </a>
                    {enrichLead.company_name && (
                      <a
                        href={`https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(enrichLead.company_name)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 px-3 py-1.5 rounded-md transition"
                        data-testid="lead-research-companies-house"
                      >
                        Companies House
                      </a>
                    )}
                    <a
                      href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(`${enrichLead.first_name || ''} ${enrichLead.last_name || ''} ${enrichLead.company_name || ''}`.trim())}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 px-3 py-1.5 rounded-md transition"
                      data-testid="lead-research-linkedin-search"
                    >
                      LinkedIn search
                    </a>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setEnrichLead(null)} data-testid="enrich-close-btn">Close</Button>
                <Button
                  onClick={() => { handleVerifyLead(enrichLead.id); setEnrichLead(null); }}
                  className="bg-[#e6007e] hover:bg-[#c4006b]"
                  data-testid="enrich-verify-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-1" /> Re-verify email
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import CSV Modal */}
      <Dialog open={showImportModal} onOpenChange={(o) => o ? setShowImportModal(true) : closeImportModal()}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Leads from CSV</DialogTitle>
            <DialogDescription className="text-gray-400">
              Upload any lead-list CSV — column headers are auto-detected (Apollo, Hunter, LinkedIn export, plain spreadsheet, etc.).
            </DialogDescription>
          </DialogHeader>

          {!importResult && (
            <>
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setImportMode('file')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${importMode === 'file' ? 'bg-[#e6007e] text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  data-testid="import-mode-file-btn"
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  onClick={() => setImportMode('paste')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${importMode === 'paste' ? 'bg-[#e6007e] text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                  data-testid="import-mode-paste-btn"
                >
                  Paste rows manually
                </button>
              </div>

              {importMode === 'file' ? (
                <div>
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('hunter-csv-file-input')?.click()}
                    className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition ${importDragActive ? 'border-[#e6007e] bg-pink-500/10' : 'border-gray-600 hover:border-gray-500 bg-gray-900/30'}`}
                    data-testid="csv-dropzone"
                  >
                    <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                    {importFile ? (
                      <>
                        <p className="text-white font-semibold mb-1">{importFile.name}</p>
                        <p className="text-gray-400 text-sm">{(importFile.size / 1024).toFixed(1)} KB — click or drop to replace</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-200 font-medium mb-1">Drop your CSV here</p>
                        <p className="text-gray-400 text-sm">or click to browse</p>
                      </>
                    )}
                    <input
                      id="hunter-csv-file-input"
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      data-testid="csv-file-input"
                    />
                  </div>
                  <div className="mt-3 text-xs text-gray-400 space-y-1">
                    <p><strong className="text-gray-300">Auto-detects:</strong> Email address, First name, Last name, Company, Job title, Industry, Website, LinkedIn URL, Phone, City, Country, Verification status, Confidence score.</p>
                    <p>Duplicates and suppressed emails are skipped automatically.</p>
                  </div>
                </div>
              ) : (
                <div>
                  <Textarea
                    placeholder="john@company.com, John, Doe, Company Ltd&#10;jane@business.com, Jane, Smith, Business Inc"
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    className="bg-gray-700 border-gray-600 min-h-[180px]"
                    data-testid="csv-paste-textarea"
                  />
                  <p className="text-xs text-gray-400 mt-2">Format: <code>email, first_name, last_name, company_name</code></p>
                </div>
              )}
            </>
          )}

          {importResult && (
            <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2 text-green-400 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Import complete</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Imported</span><span className="text-white font-semibold">{importResult.imported || 0}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Duplicates skipped</span><span className="text-white">{importResult.skipped_duplicate || 0}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Suppressed skipped</span><span className="text-white">{importResult.skipped_suppressed || 0}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Invalid rows</span><span className="text-white">{importResult.skipped_invalid || 0}</span></div>
              </div>
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mt-3 text-xs text-red-400">
                  <p className="font-semibold mb-1">{importResult.errors.length} error(s):</p>
                  <ul className="list-disc list-inside max-h-24 overflow-y-auto">
                    {importResult.errors.slice(0, 5).map((er, i) => (
                      <li key={i}>{er.email}: {er.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeImportModal} data-testid="import-cancel-btn">
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button
                onClick={handleImportLeads}
                disabled={importLoading || (importMode === 'file' && !importFile) || (importMode === 'paste' && !importData.trim())}
                className="bg-[#e6007e] hover:bg-[#c4006b] disabled:opacity-50"
                data-testid="import-submit-btn"
              >
                {importLoading ? 'Importing…' : 'Import'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hunter.io Search Modal */}
      <Dialog open={showHunterModal} onOpenChange={setShowHunterModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Hunter.io Domain Search</DialogTitle>
            <DialogDescription className="text-gray-400">
              Find email addresses at a company domain
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-gray-400">Domain</Label>
            <Input
              placeholder="example.com"
              value={hunterDomain}
              onChange={(e) => setHunterDomain(e.target.value)}
              className="bg-gray-700 border-gray-600"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowHunterModal(false)}>Cancel</Button>
            <Button onClick={handleHunterSearch} className="bg-[#e6007e] hover:bg-[#c4006b]">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== TEMPLATES PAGE ====================

export const OutreachTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', body_html: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [aiPrompt, setAIPrompt] = useState({
    business_context: '',
    pain_points: '',
    value_proposition: 'Paify helps UK SMEs get paid faster with automated payment collection, contract signing, and payment protection.',
    tone: 'professional',
    max_length: 150
  });
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchTemplates();
  }, [adminToken, navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/templates`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch templates",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await axios.post(`${API}/api/admin/outreach/templates`, newTemplate, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Template created" });
      setShowCreateModal(false);
      setNewTemplate({ name: '', subject: '', body_html: '' });
      fetchTemplates();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive"
      });
    }
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/api/admin/outreach/templates/generate-ai`, aiPrompt, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setGeneratedEmail(response.data.generated);
      toast({ title: "Success", description: "Email generated with AI" });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to generate email",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleUseGenerated = () => {
    if (generatedEmail) {
      setNewTemplate({
        name: `AI Generated - ${new Date().toLocaleDateString()}`,
        subject: generatedEmail.subject,
        body_html: generatedEmail.body_html
      });
      setShowAIModal(false);
      setShowCreateModal(true);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/outreach/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Template deleted" });
      fetchTemplates();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive"
      });
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      id: template.id,
      name: template.name || '',
      subject: template.subject || '',
      body_html: template.body_html || template.body_text || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;
    try {
      await axios.put(
        `${API}/api/admin/outreach/templates/${editingTemplate.id}`,
        {
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          body_html: editingTemplate.body_html,
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: 'Saved', description: 'Template updated' });
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    }
  };


  const [sendingTest, setSendingTest] = useState(null); // template id currently being test-sent

  const handleSendTest = async (template) => {
    const defaultTo = adminUser?.email || '';
    const to = window.prompt(`Send a test of "${template.name}" to which email?\n\nLeave blank to use ${defaultTo || 'your admin email'}.`, defaultTo);
    if (to === null) return; // cancelled
    const recipient = (to || '').trim() || defaultTo;
    if (!recipient || !recipient.includes('@')) {
      toast({ title: 'Invalid recipient', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    setSendingTest(template.id);
    try {
      const r = await axios.post(
        `${API}/api/admin/outreach/templates/${template.id}/send-test`,
        { to_email: recipient },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      if (r.data?.success) {
        toast({ title: 'Test email sent', description: `Delivered to ${recipient} — check your inbox (and spam).` });
      } else {
        toast({ title: 'Send failed', description: r.data?.error || 'SMTP rejected the message.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingTest(null);
    }
  };

  const handlePreviewTemplate = async (template) => {
    setPreviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/preview-email`,
        {
          template_id: template.id,
          first_name: "James",
          last_name: "Wilson",
          company_name: "Smith & Co Plumbing",
          position: "Managing Director",
          email: "james@smithplumbing.co.uk",
          industry: "plumbing"
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      setPreviewHtml(response.data.preview.full_preview);
      setShowPreviewModal(true);
    } catch (err) {
      toast({ title: "Error", description: "Failed to generate preview", variant: "destructive" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {!new URLSearchParams(window.location.search).has('from') && (
        <>
          <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Send className="w-4 h-4 inline mr-2" />Campaigns
            </button>
            <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <FileText className="w-4 h-4 inline mr-2" />Templates
            </button>
            <button onClick={() => navigate('/admin/outreach/referrals')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Share2 className="w-4 h-4 inline mr-2" />Referrals
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>
        </>
      )}
      {new URLSearchParams(window.location.search).has('from') && (
        <ReachEmbedNav active="templates" />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Email Templates</h1>
            <p className="text-gray-400">Create and manage outreach templates</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => setShowAIModal(true)} className="border-gray-600 text-gray-300">
              <Sparkles className="w-4 h-4 mr-2" />
              Generate with AI
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-[#e6007e] hover:bg-[#c4006b]">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center justify-between">
                  {template.name}
                  {template.ai_generated && (
                    <Sparkles className="w-4 h-4 text-[#e6007e]" />
                  )}
                </CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  Subject: {template.subject}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-gray-300 text-sm mb-4 line-clamp-3" 
                  dangerouslySetInnerHTML={{ __html: template.body_html }} 
                />
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {template.personalization_tokens?.slice(0, 3).map((token) => (
                      <span key={token} className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded">
                        {`{{${token}}}`}
                      </span>
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); handlePreviewTemplate(template); }}
                      className="text-blue-400 hover:text-blue-300"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEditTemplate(template)} className="text-[#e6007e] hover:text-pink-300" title="Edit" data-testid={`template-edit-btn-${template.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleSendTest(template); }}
                      disabled={sendingTest === template.id}
                      className="text-emerald-400 hover:text-emerald-300"
                      title="Send a test email of this template to me"
                      data-testid={`template-send-test-btn-${template.id}`}
                    >
                      <Send className={`w-4 h-4 ${sendingTest === template.id ? 'animate-pulse' : ''}`} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(template.id)} title="Delete">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {templates.length === 0 && (
            <div className="col-span-3 text-center py-12 text-gray-400">
              No templates yet. Create one to get started.
            </div>
          )}
        </div>
      </main>

      {/* Edit Template Modal */}
      <Dialog open={!!editingTemplate} onOpenChange={(o) => !o && setEditingTemplate(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl" data-testid="template-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit template</DialogTitle>
            <DialogDescription className="text-gray-400">Use {'{{first_name}}'} and {'{{company_name}}'} tokens to personalise.</DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-400 text-xs">Name</Label>
                <Input value={editingTemplate.name} onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" data-testid="template-edit-name" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Subject</Label>
                <Input value={editingTemplate.subject} onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" data-testid="template-edit-subject" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Body</Label>
                <div className="mt-1" data-testid="template-edit-body">
                  <RichBodyEditor
                    value={editingTemplate.body_html || ''}
                    onChange={(html) => setEditingTemplate({ ...editingTemplate, body_html: html })}
                    placeholder="Hi {{first_name}}, ..."
                    minHeight={240}
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Tokens: {`{{first_name}}, {{last_name}}, {{company_name}}`} — they auto-fill at send time.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => editingTemplate && handleSendTest(editingTemplate)}
              disabled={sendingTest === editingTemplate?.id}
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 mr-auto"
              data-testid="template-edit-send-test"
            >
              <Send className={`w-4 h-4 mr-1.5 ${sendingTest === editingTemplate?.id ? 'animate-pulse' : ''}`} />
              {sendingTest === editingTemplate?.id ? 'Sending…' : 'Send test to me'}
            </Button>
            <Button variant="ghost" onClick={() => setEditingTemplate(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-[#e6007e] hover:bg-[#c4006b]" data-testid="template-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Eye className="w-5 h-5 mr-2 text-blue-400" />
              Template Preview
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Preview with sample recipient data
            </DialogDescription>
          </DialogHeader>
          <div 
            className="mt-4 bg-white rounded-lg"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPreviewModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Template Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white font-medium">Template Name</Label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder="Cold Outreach v1"
                className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
            </div>
            <div>
              <Label className="text-white font-medium">Subject Line</Label>
              <Input
                value={newTemplate.subject}
                onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})}
                placeholder="Quick question about {{company_name}}"
                className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
            </div>
            <div>
              <Label className="text-white font-medium">Email Body</Label>
              <div className="mt-1">
                <RichBodyEditor
                  value={newTemplate.body_html}
                  onChange={(html) => setNewTemplate({ ...newTemplate, body_html: html })}
                  placeholder="Hi {{first_name}}, ..."
                  minHeight={240}
                />
              </div>
              <p className="text-gray-400 text-xs mt-1">
                Available tokens: {`{{first_name}}, {{last_name}}, {{company_name}}, {{industry}}`}
              </p>
            </div>
          </div>
          <DialogFooter className="flex space-x-3">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-gray-500 text-gray-300 hover:bg-gray-700">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} className="bg-[#e6007e] hover:bg-[#c4006b]">Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generation Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-[#e6007e]" />
              AI Email Generator
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Generate personalized outreach emails powered by GPT
            </DialogDescription>
          </DialogHeader>
          
          {!generatedEmail ? (
            <div className="space-y-4">
              <div>
                <Label className="text-white font-medium">Target Business Context</Label>
                <Textarea
                  value={aiPrompt.business_context}
                  onChange={(e) => setAIPrompt({...aiPrompt, business_context: e.target.value})}
                  placeholder="UK-based web design agency with 5-10 employees, handles multiple client projects..."
                  className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                />
              </div>
              <div>
                <Label className="text-white font-medium">Their Pain Points (optional)</Label>
                <Input
                  value={aiPrompt.pain_points}
                  onChange={(e) => setAIPrompt({...aiPrompt, pain_points: e.target.value})}
                  placeholder="Late payments, chasing invoices, contract admin..."
                  className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                />
              </div>
              <div>
                <Label className="text-white font-medium">Tone</Label>
                <Select value={aiPrompt.tone} onValueChange={(v) => setAIPrompt({...aiPrompt, tone: v})}>
                  <SelectTrigger className="bg-white text-gray-900 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="flex space-x-3">
                <Button variant="outline" onClick={() => setShowAIModal(false)} className="border-gray-500 text-gray-300 hover:bg-gray-700">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleGenerateAI} disabled={generating} className="bg-[#e6007e] hover:bg-[#c4006b]">
                  {generating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Email
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-white font-medium">Generated Subject</Label>
                <div className="bg-white text-gray-900 p-3 rounded border">{generatedEmail.subject}</div>
              </div>
              <div>
                <Label className="text-white font-medium">Generated Body</Label>
                <div className="bg-white text-gray-900 p-3 rounded border prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: generatedEmail.body_html }}
                />
              </div>
              <DialogFooter className="flex space-x-3">
                <Button variant="outline" onClick={() => setGeneratedEmail(null)} className="border-gray-500 text-gray-300 hover:bg-gray-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                <Button onClick={handleUseGenerated} className="bg-[#e6007e] hover:bg-[#c4006b]">
                  Use This Template
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== CAMPAIGNS PAGE ====================

export const OutreachCampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [complianceChecklist, setComplianceChecklist] = useState({});
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    steps: [],
    daily_send_limit: 50,
    stop_on_reply: true
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [adminToken, navigate]);

  const fetchData = async () => {
    try {
      const [campaignsRes, templatesRes, leadsRes] = await Promise.all([
        axios.get(`${API}/api/admin/outreach/campaigns`, { headers: { Authorization: `Bearer ${adminToken}` }}),
        axios.get(`${API}/api/admin/outreach/templates`, { headers: { Authorization: `Bearer ${adminToken}` }}),
        axios.get(`${API}/api/admin/outreach/leads?status=verified`, { headers: { Authorization: `Bearer ${adminToken}` }})
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setTemplates(templatesRes.data.templates || []);
      setLeads(leadsRes.data.leads || []);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const response = await axios.post(`${API}/api/admin/outreach/campaigns`, {
        name: newCampaign.name,
        description: newCampaign.description,
        steps: newCampaign.steps,
        daily_send_limit: newCampaign.daily_send_limit,
        stop_on_reply: newCampaign.stop_on_reply,
        target_filters: { status: { "$in": ["new", "verified"] } }
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Campaign created" });
      setShowCreateModal(false);
      setNewCampaign({ name: '', description: '', steps: [], daily_send_limit: 50, stop_on_reply: true });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to create campaign",
        variant: "destructive"
      });
    }
  };

  const handleOpenCompliance = (campaign) => {
    setSelectedCampaign(campaign);
    setComplianceChecklist({});
    setShowComplianceModal(true);
  };

  const handleSubmitCompliance = async () => {
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/campaigns/${selectedCampaign.id}/compliance`,
        complianceChecklist,
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      if (response.data.approved) {
        toast({ title: "Success", description: "Compliance approved! Campaign ready to start." });
        setShowComplianceModal(false);
        fetchData();
      } else {
        toast({
          title: "Incomplete",
          description: response.data.message,
          variant: "destructive"
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to submit compliance",
        variant: "destructive"
      });
    }
  };

  const handleStartCampaign = async (campaignId, sendNow = false) => {
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/start`,
        { send_now: sendNow },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      const data = response.data || {};
      const inWindow = data.send_window?.in_window;
      const nextOpen = data.send_window?.next_open_iso;
      let title;
      if (sendNow) {
        title = `${data.sent_now || 0} sent · ${data.emails_queued || 0} queued`;
      } else if (inWindow) {
        title = `${data.emails_queued || 0} queued — firing soon`;
      } else {
        title = `${data.emails_queued || 0} queued — window CLOSED`;
      }
      toast({
        title,
        description: data.message,
        variant: !sendNow && !inWindow && (data.emails_queued || 0) > 0 ? 'default' : 'default',
        duration: 7000,
      });
      // If queued but window closed and user didn't pick send-now, offer one-click bypass
      if (!sendNow && !inWindow && (data.emails_queued || 0) > 0) {
        const bypass = window.confirm(
          `Send window is closed${nextOpen ? ` until ${new Date(nextOpen).toLocaleString('en-GB')}` : ''}. ` +
          `Send the ${data.emails_queued} email(s) RIGHT NOW (bypass window)?`
        );
        if (bypass) {
          // Re-trigger the queue processor directly so we don't double-queue
          try {
            const r2 = await axios.post(`${API}/api/admin/outreach/process-queue`, {}, {
              headers: { Authorization: `Bearer ${adminToken}` },
            });
            toast({ title: `Sent now`, description: `${r2.data?.emails_sent || 0} sent.` });
          } catch (err2) {
            toast({ title: 'Send now failed', description: err2.response?.data?.detail || err2.message, variant: 'destructive' });
          }
        }
      }
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to start campaign",
        variant: "destructive"
      });
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/pause`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast({ title: "Success", description: "Campaign paused" });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to pause campaign",
        variant: "destructive"
      });
    }
  };

  const handleProcessQueue = async () => {
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/process-queue`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast({ 
        title: "Success", 
        description: `Processed queue: ${response.data.emails_sent} emails sent`
      });
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to process queue",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      pending_compliance: 'bg-yellow-500',
      approved: 'bg-blue-500',
      active: 'bg-green-500',
      paused: 'bg-orange-500',
      completed: 'bg-purple-500',
      cancelled: 'bg-red-500'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs text-white ${colors[status] || 'bg-gray-500'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <Send className="w-4 h-4 inline mr-2" />Campaigns
            </button>
            <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <FileText className="w-4 h-4 inline mr-2" />Templates
            </button>
            <button onClick={() => navigate('/admin/outreach/referrals')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Share2 className="w-4 h-4 inline mr-2" />Referrals
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Campaigns</h1>
            <p className="text-gray-400">Manage your outreach campaigns</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={handleProcessQueue} className="border-gray-600 text-gray-300">
              <RefreshCw className="w-4 h-4 mr-2" />
              Process Queue
            </Button>
            <Button onClick={() => {
              // Default to a single email — manual control by default
              setNewCampaign({
                name: '',
                description: '',
                steps: [{ step_number: 1, delay_days: 0, template_id: '' }],
                daily_send_limit: 50,
                stop_on_reply: true,
              });
              setShowCreateModal(true);
            }} className="bg-[#e6007e] hover:bg-[#c4006b]">
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>

        {/* Campaigns List */}
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <Card 
              key={campaign.id} 
              className="bg-gray-800 border-gray-700 hover:border-[#e6007e]/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/admin/outreach/campaigns/${campaign.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">{campaign.name}</h3>
                      {getStatusBadge(campaign.status)}
                      {campaign.steps && campaign.steps.length > 1 && (
                        <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                          {campaign.steps.length}-step sequence
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm mb-4">{campaign.description || 'No description'}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Recipients</p>
                        <p className="text-white font-medium">{campaign.total_recipients}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Sent</p>
                        <p className="text-white font-medium">{campaign.emails_sent}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Opened</p>
                        <p className="text-green-400 font-medium">{campaign.emails_opened}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Replied</p>
                        <p className="text-purple-400 font-medium">{campaign.emails_replied}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Daily Limit</p>
                        <p className="text-white font-medium">{campaign.daily_send_limit}/day</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 ml-4" onClick={(e) => e.stopPropagation()}>
                    {campaign.status === 'pending_compliance' && (
                      <Button size="sm" onClick={() => handleOpenCompliance(campaign)} className="bg-yellow-500 hover:bg-yellow-600">
                        <Shield className="w-4 h-4 mr-2" />
                        Compliance
                      </Button>
                    )}
                    {campaign.status === 'approved' && (
                      <div className="flex flex-col items-end gap-1">
                        <Button size="sm" onClick={() => handleStartCampaign(campaign.id, true)} className="bg-[#e6007e] hover:bg-[#c4006b]" data-testid={`campaign-send-now-btn-${campaign.id}`} title="Queue and fire immediately">
                          <Zap className="w-4 h-4 mr-2" />
                          Send to all now
                        </Button>
                        <button onClick={() => handleStartCampaign(campaign.id, false)} className="text-xs text-gray-400 hover:text-gray-200 underline" data-testid={`campaign-start-btn-${campaign.id}`}>
                          Or schedule for next window
                        </button>
                      </div>
                    )}
                    {campaign.status === 'active' && (
                      <Button size="sm" onClick={() => handlePauseCampaign(campaign.id)} variant="outline" className="border-orange-500 text-orange-500">
                        <Pause className="w-4 h-4 mr-2" />
                        Pause
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {campaigns.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              No campaigns yet. Create one to start outreach.
            </div>
          )}
        </div>
      </main>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription className="text-gray-400">
              Set up a single email or multi-step sequence
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            <div>
              <Label className="text-gray-400">Campaign Name *</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="Q1 SME Outreach"
                className="bg-gray-700 border-gray-600"
              />
            </div>
            <div>
              <Label className="text-gray-400">Description</Label>
              <Textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                placeholder="Targeting UK web agencies..."
                className="bg-gray-700 border-gray-600"
              />
            </div>
            
            {/* Email Sequence */}
            <div className="border border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-white font-medium">Email Sequence</Label>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => {
                    const steps = newCampaign.steps || [];
                    setNewCampaign({
                      ...newCampaign, 
                      steps: [...steps, { 
                        step_number: steps.length + 1, 
                        delay_days: steps.length === 0 ? 0 : 3, 
                        template_id: '' 
                      }]
                    });
                  }}
                  className="border-[#e6007e] text-[#e6007e] hover:bg-[#e6007e]/10"
                >
                  <Plus className="w-4 h-4 mr-1" />Add Step
                </Button>
              </div>
              
              {(!newCampaign.steps || newCampaign.steps.length === 0) ? (
                <div className="text-center py-6 text-gray-400">
                  <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No steps added yet</p>
                  <p className="text-sm">Click "Add Step" to create your email sequence</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newCampaign.steps.map((step, idx) => (
                    <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-700/50 rounded-lg">
                      <div className="w-8 h-8 bg-[#e6007e] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {step.step_number}
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Select 
                          value={step.template_id} 
                          onValueChange={(v) => {
                            const steps = [...newCampaign.steps];
                            steps[idx].template_id = v;
                            setNewCampaign({...newCampaign, steps});
                          }}
                        >
                          <SelectTrigger className="bg-gray-600 border-gray-500">
                            <SelectValue placeholder="Select template" />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400 text-sm whitespace-nowrap">
                            {idx === 0 ? 'Send:' : 'Wait:'}
                          </span>
                          {idx === 0 ? (
                            <span className="text-white text-sm">Immediately</span>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <Input
                                type="number"
                                value={step.delay_days}
                                onChange={(e) => {
                                  const steps = [...newCampaign.steps];
                                  steps[idx].delay_days = parseInt(e.target.value) || 1;
                                  setNewCampaign({...newCampaign, steps});
                                }}
                                min="1"
                                max="30"
                                className="bg-gray-600 border-gray-500 w-16"
                              />
                              <span className="text-gray-400 text-sm">days</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          const steps = newCampaign.steps.filter((_, i) => i !== idx)
                            .map((s, i) => ({...s, step_number: i + 1}));
                          setNewCampaign({...newCampaign, steps});
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Quick presets */}
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-gray-500 text-xs mb-2">Quick Presets:</p>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      if (templates.length > 0) {
                        setNewCampaign({
                          ...newCampaign,
                          steps: [
                            { step_number: 1, delay_days: 0, template_id: templates[0]?.id || '' },
                            { step_number: 2, delay_days: 3, template_id: templates[0]?.id || '' },
                            { step_number: 3, delay_days: 7, template_id: templates[0]?.id || '' }
                          ]
                        });
                      }
                    }}
                    className="text-xs border-gray-600 text-gray-300"
                  >
                    3-Step (Day 1, 3, 7)
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      if (templates.length > 0) {
                        setNewCampaign({
                          ...newCampaign,
                          steps: [
                            { step_number: 1, delay_days: 0, template_id: templates[0]?.id || '' }
                          ]
                        });
                      }
                    }}
                    className="text-xs border-gray-600 text-gray-300"
                  >
                    Single Email
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Daily Send Limit</Label>
                <Input
                  type="number"
                  value={newCampaign.daily_send_limit}
                  onChange={(e) => setNewCampaign({...newCampaign, daily_send_limit: parseInt(e.target.value) || 50})}
                  min="1"
                  max="200"
                  className="bg-gray-700 border-gray-600"
                />
              </div>
              <div>
                <Label className="text-gray-400">Behavior</Label>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="stop_on_reply"
                      checked={newCampaign.stop_on_reply !== false}
                      onCheckedChange={(checked) => setNewCampaign({...newCampaign, stop_on_reply: checked})}
                    />
                    <label htmlFor="stop_on_reply" className="text-sm text-gray-300">Stop on reply</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateCampaign} 
              disabled={!newCampaign.name || !newCampaign.steps || newCampaign.steps.length === 0 || newCampaign.steps.some(s => !s.template_id)}
              className="bg-[#e6007e] hover:bg-[#c4006b]"
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compliance Modal */}
      <Dialog open={showComplianceModal} onOpenChange={setShowComplianceModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-yellow-500" />
              GDPR/PECR Compliance Checklist
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              All items must be checked before the campaign can be approved.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {[
              { key: 'legitimate_interest', label: 'Legitimate Interest Basis', desc: 'This outreach is based on legitimate business interest (B2B communication to corporate emails)' },
              { key: 'business_email_only', label: 'Business Emails Only', desc: 'All recipients are business email addresses (not personal emails like @gmail.com)' },
              { key: 'clear_identity', label: 'Clear Sender Identity', desc: 'The email clearly identifies Paify as the sender with valid contact details' },
              { key: 'opt_out_provided', label: 'Easy Opt-Out', desc: 'Every email includes a clear, working unsubscribe mechanism' },
              { key: 'relevant_content', label: 'Relevant Content', desc: "The email content is relevant to the recipient's business activities" },
              { key: 'no_misleading_subject', label: 'Honest Subject Line', desc: 'The subject line accurately reflects the email content (no clickbait)' },
              { key: 'contact_details_provided', label: 'Contact Details Included', desc: "The email includes Paify's registered business address and contact information" },
              { key: 'data_minimization', label: 'Data Minimization', desc: 'Only necessary personal data is collected and processed for this campaign' }
            ].map((item) => (
              <div key={item.key} className="flex items-start space-x-3 p-3 rounded bg-gray-700/50">
                <Checkbox
                  id={item.key}
                  checked={complianceChecklist[item.key] || false}
                  onCheckedChange={(checked) => setComplianceChecklist({...complianceChecklist, [item.key]: checked})}
                  className="mt-1"
                />
                <div>
                  <label htmlFor={item.key} className="text-white font-medium cursor-pointer">{item.label}</label>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowComplianceModal(false)}>Cancel</Button>
            <Button onClick={handleSubmitCompliance} className="bg-green-500 hover:bg-green-600">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve Compliance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== CAMPAIGN DETAIL PAGE ====================

export const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [templates, setTemplates] = useState({});
  const [stepStats, setStepStats] = useState([]);
  const [leadProgress, setLeadProgress] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddLeadsModal, setShowAddLeadsModal] = useState(false);
  const [quickAddEmail, setQuickAddEmail] = useState('');
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddCompany, setQuickAddCompany] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchCampaignDetail();
  }, [adminToken, campaignId]);

  const fetchCampaignDetail = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCampaign(response.data.campaign);
      setTemplates(response.data.templates || {});
      setStepStats(response.data.step_stats || []);
      setLeadProgress(response.data.lead_progress || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (err) {
      toast({ title: "Error", description: "Failed to load campaign", variant: "destructive" });
      navigate('/admin/outreach/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAddLead = async () => {
    const email = quickAddEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email.', variant: 'destructive' });
      return;
    }
    setQuickAddLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/quick-add`,
        {
          email,
          full_name: quickAddName || null,
          company_name: quickAddCompany || null,
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({
        title: r.data.enrolled ? 'Lead added' : 'Already enrolled',
        description: r.data.message,
      });
      setQuickAddEmail('');
      setQuickAddName('');
      setQuickAddCompany('');
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not add', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleSendStepNow = async (stepNumber) => {
    if (!window.confirm(`Send step ${stepNumber} to all eligible leads now? Replied / unsubscribed / bounced leads are skipped automatically.`)) return;
    try {
      const r = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/send-next-step`,
        { step: stepNumber },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({
        title: 'Queued',
        description: r.data?.message || `${r.data?.queued || 0} email(s) queued.`,
      });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not send', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleSendNextToLead = async (leadId, leadEmail) => {
    if (!window.confirm(`Send the next step to ${leadEmail} now?`)) return;
    try {
      const r = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/leads/${leadId}/send-next`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: 'Queued', description: r.data?.message || 'Queued.' });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not send', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleStartFromDetail = async (sendNow = false) => {
    try {
      const r = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/start`,
        { send_now: sendNow },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const data = r.data || {};
      const inWindow = data.send_window?.in_window;
      let title;
      if (sendNow) title = `${data.sent_now || 0} sent · ${data.emails_queued || 0} queued`;
      else if (inWindow) title = `${data.emails_queued || 0} queued — firing soon`;
      else title = `${data.emails_queued || 0} queued — window CLOSED`;
      toast({ title, description: data.message, duration: 7000 });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not start', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handlePauseFromDetail = async () => {
    try {
      await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/pause`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: 'Campaign paused' });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not pause', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleResumeFromDetail = async () => {
    try {
      await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/resume`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: 'Campaign resumed' });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not resume', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleProcessQueueFromDetail = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/outreach/process-queue`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast({ title: 'Queue processed', description: `${r.data?.emails_sent || 0} sent.` });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not process', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleRemoveLeadFromCampaign = async (leadId, leadEmail) => {
    if (!window.confirm(`Remove ${leadEmail} from this campaign? Their queued and future emails will be cancelled.`)) return;
    try {
      const r = await axios.delete(`${API}/api/admin/outreach/campaigns/${campaignId}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast({
        title: 'Removed',
        description: `${leadEmail} removed${r.data?.queued_emails_cancelled ? ` · ${r.data.queued_emails_cancelled} queued email(s) cancelled` : ''}.`,
      });
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: 'Could not remove', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const fetchAvailableLeads = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/leads?status=verified&limit=100`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      // Filter out leads already in this campaign
      const campaignLeadIds = leadProgress.map(lp => lp.lead_id);
      const available = (response.data.leads || []).filter(l => !campaignLeadIds.includes(l.id));
      setAvailableLeads(available);
    } catch (err) {
      console.error('Failed to fetch leads', err);
    }
  };

  const handleAddLeads = async () => {
    if (selectedLeadIds.length === 0) return;
    
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/campaigns/${campaignId}/add-leads`,
        selectedLeadIds,
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      toast({ title: "Success", description: response.data.message });
      setShowAddLeadsModal(false);
      setSelectedLeadIds([]);
      fetchCampaignDetail();
    } catch (err) {
      toast({ title: "Error", description: "Failed to add leads", variant: "destructive" });
    }
  };

  const handleDeleteCampaign = async () => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/outreach/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Campaign deleted" });
      navigate('/admin/outreach/campaigns');
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to delete", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      pending_compliance: 'bg-yellow-500',
      approved: 'bg-blue-500',
      active: 'bg-green-500',
      paused: 'bg-orange-500',
      completed: 'bg-purple-500',
      cancelled: 'bg-red-500'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm text-white ${colors[status] || 'bg-gray-500'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const getLeadStatusBadge = (status) => {
    const colors = {
      active: 'bg-blue-500',
      completed: 'bg-green-500',
      replied: 'bg-purple-500',
      bounced: 'bg-red-500',
      unsubscribed: 'bg-orange-500',
      stopped: 'bg-gray-500'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs text-white ${colors[status] || 'bg-gray-500'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#e6007e] animate-spin" />
      </div>
    );
  }

  if (!campaign) return null;

  const totalLeads = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const openRate = campaign.emails_sent > 0 ? Math.round(campaign.emails_opened / campaign.emails_sent * 100) : 0;
  const replyRate = campaign.emails_sent > 0 ? Math.round(campaign.emails_replied / campaign.emails_sent * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button & Title */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/outreach/campaigns')} className="text-gray-400">
              <ChevronLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">{campaign.name}</h1>
                {getStatusBadge(campaign.status)}
              </div>
              <p className="text-gray-400 text-sm mt-1">{campaign.description || 'No description'}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {campaign.status === 'draft' && (
              <Button variant="outline" onClick={handleDeleteCampaign} className="border-red-500 text-red-500 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4 mr-2" />Delete
              </Button>
            )}
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-white">{totalLeads}</p>
              <p className="text-gray-400 text-sm">Total Leads</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{campaign.emails_sent}</p>
              <p className="text-gray-400 text-sm">Emails Sent</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{openRate}%</p>
              <p className="text-gray-400 text-sm">Open Rate</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400">{replyRate}%</p>
              <p className="text-gray-400 text-sm">Reply Rate</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#e6007e]">{campaign.leads_converted || 0}</p>
              <p className="text-gray-400 text-sm">Converted</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Sequence Steps */}
          <div className="md:col-span-2">
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-[#e6007e]" />
                  Email Sequence ({campaign.steps?.length || 1} steps)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {campaign.steps && campaign.steps.length > 0 ? (
                  <div className="space-y-4">
                    {campaign.steps.map((step, idx) => {
                      const stats = stepStats.find(s => s.step_number === step.step_number) || {};
                      const template = templates[step.template_id] || {};
                      return (
                        <div key={idx} className="p-4 bg-gray-700/50 rounded-lg border-l-4 border-[#e6007e]">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <span className="w-8 h-8 bg-[#e6007e] rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {step.step_number}
                              </span>
                              <div>
                                <p className="text-white font-medium">{template.name || 'Email Step'}</p>
                                <p className="text-gray-400 text-sm">
                                  {step.delay_days === 0 ? 'Immediate' : `Day ${step.delay_days}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-4 text-sm">
                              <div className="text-center">
                                <p className="text-white font-medium">{stats.sent || 0}</p>
                                <p className="text-gray-500">Sent</p>
                              </div>
                              <div className="text-center">
                                <p className="text-green-400 font-medium">{stats.open_rate || 0}%</p>
                                <p className="text-gray-500">Opened</p>
                              </div>
                              <div className="text-center">
                                <p className="text-purple-400 font-medium">{stats.replied || 0}</p>
                                <p className="text-gray-500">Replied</p>
                              </div>
                            </div>
                          </div>
                          {template.subject && (
                            <p className="text-gray-400 text-sm mt-2 pl-11">
                              Subject: <span className="text-gray-300">{template.subject}</span>
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <p>Single email campaign (no sequence)</p>
                    {campaign.template_id && templates[campaign.template_id] && (
                      <p className="mt-2">Template: {templates[campaign.template_id].name}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lead Progress Table */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center">
                    <Users className="w-5 h-5 mr-2 text-[#e6007e]" />
                    Leads ({totalLeads})
                  </span>
                  <div className="flex space-x-2 text-sm font-normal">
                    {Object.entries(statusCounts).map(([status, count]) => (
                      <span key={status} className="px-2 py-1 bg-gray-700 rounded text-gray-300">
                        {status}: {count}
                      </span>
                    ))}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Inline quick-add: type email → Enter → done */}
                <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-3 mb-4" data-testid="quick-add-row">
                  <p className="text-xs text-gray-400 mb-2">Add a lead — paste an email and press Enter. Optional: name + company.</p>
                  <div className="flex flex-wrap gap-2 items-stretch">
                    <Input
                      type="email"
                      value={quickAddEmail}
                      onChange={(e) => setQuickAddEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleQuickAddLead()}
                      placeholder="email@example.com (required)"
                      className="bg-gray-800 border-gray-600 text-white flex-1 min-w-[220px] h-9"
                      data-testid="quick-add-email"
                    />
                    <Input
                      value={quickAddName}
                      onChange={(e) => setQuickAddName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleQuickAddLead()}
                      placeholder="Name (optional)"
                      className="bg-gray-800 border-gray-600 text-white w-44 h-9"
                      data-testid="quick-add-name"
                    />
                    <Input
                      value={quickAddCompany}
                      onChange={(e) => setQuickAddCompany(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleQuickAddLead()}
                      placeholder="Company (optional)"
                      className="bg-gray-800 border-gray-600 text-white w-44 h-9"
                      data-testid="quick-add-company"
                    />
                    <Button
                      onClick={handleQuickAddLead}
                      disabled={quickAddLoading || !quickAddEmail.trim()}
                      className="bg-[#e6007e] hover:bg-[#c4006b] h-9"
                      data-testid="quick-add-submit"
                    >
                      {quickAddLoading ? 'Adding…' : 'Add'}
                    </Button>
                  </div>
                </div>

                {leadProgress.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2">Email</th>
                          <th className="text-center py-2">Step</th>
                          <th className="text-center py-2">Status</th>
                          <th className="text-right py-2">Enrolled</th>
                          <th className="text-right py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadProgress.slice(0, 20).map((lp) => (
                          <tr key={lp.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                            <td className="py-2 text-white">{lp.lead_email}</td>
                            <td className="py-2 text-center text-gray-300">{lp.current_step} / {campaign.steps?.length || 1}</td>
                            <td className="py-2 text-center">{getLeadStatusBadge(lp.status)}</td>
                            <td className="py-2 text-right text-gray-400">
                              {new Date(lp.enrolled_at).toLocaleDateString()}
                            </td>
                            <td className="py-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSendNextToLead(lp.lead_id, lp.lead_email)}
                                  title="Send next step to this lead now"
                                  className="text-[#e6007e] hover:text-[#ff1a8e] h-7 px-2 text-xs"
                                  data-testid={`send-next-${lp.lead_id}`}
                                >
                                  Send next
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveLeadFromCampaign(lp.lead_id, lp.lead_email)}
                                  title="Remove from campaign (cancels queued emails)"
                                  className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                                  data-testid={`remove-lead-${lp.lead_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {leadProgress.length > 20 && (
                      <p className="text-center text-gray-500 text-sm mt-4">
                        Showing 20 of {totalLeads} leads
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No leads enrolled yet — use the box above to add the first one.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Settings Sidebar */}
          <div>
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-sm">Campaign Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Daily Limit</span>
                  <span className="text-white">{campaign.daily_send_limit}/day</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Send Window</span>
                  <span className="text-white">{campaign.send_start_time} - {campaign.send_end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Stop on Reply</span>
                  <span className={campaign.stop_on_reply ? 'text-green-400' : 'text-gray-500'}>
                    {campaign.stop_on_reply ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Stop on Bounce</span>
                  <span className={campaign.stop_on_bounce ? 'text-green-400' : 'text-gray-500'}>
                    {campaign.stop_on_bounce ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Created</span>
                  <span className="text-white">{new Date(campaign.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {campaign.status === 'approved' && (
                  <>
                    <Button onClick={() => handleStartFromDetail(true)} className="w-full bg-[#e6007e] hover:bg-[#c4006b] h-11" data-testid="detail-send-now-btn" title="Queue and fire immediately">
                      <Zap className="w-4 h-4 mr-2" />Send to all now
                    </Button>
                    <button onClick={() => handleStartFromDetail(false)} className="w-full text-xs text-gray-400 hover:text-gray-200 underline mt-1" data-testid="detail-start-btn">
                      Or schedule for next window (Mon-Fri 8-11am UK)
                    </button>
                  </>
                )}
                {campaign.status === 'active' && (
                  <>
                    <Button onClick={handleProcessQueueFromDetail} className="w-full bg-[#e6007e] hover:bg-[#c4006b]" data-testid="detail-process-queue-btn">
                      <Zap className="w-4 h-4 mr-2" />Process queue now
                    </Button>
                    <Button onClick={handlePauseFromDetail} variant="outline" className="w-full border-orange-500 text-orange-500" data-testid="detail-pause-btn">
                      <Pause className="w-4 h-4 mr-2" />Pause Campaign
                    </Button>
                  </>
                )}
                {campaign.status === 'paused' && (
                  <Button onClick={handleResumeFromDetail} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="detail-resume-btn">
                    <Play className="w-4 h-4 mr-2" />Resume Campaign
                  </Button>
                )}
                <Button variant="outline" className="w-full border-gray-600 text-gray-300" onClick={() => navigate('/admin/outreach/campaigns')}>
                  <ChevronLeft className="w-4 h-4 mr-2" />Back to Campaigns
                </Button>
              </CardContent>
            </Card>

            {/* Manual sequence control */}
            {campaign.steps && campaign.steps.length > 1 && (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Send className="w-4 h-4 text-[#e6007e]" />
                    Manual follow-ups
                  </CardTitle>
                  <p className="text-xs text-gray-400 mt-1">
                    You decide when each step fires. Replied / unsubscribed leads are auto-skipped.
                  </p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {campaign.steps.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-sm text-gray-300 flex-1">Step {idx + 1}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendStepNow(idx + 1)}
                        className="border-[#e6007e] text-[#e6007e] hover:bg-[#e6007e]/10 text-xs"
                        data-testid={`send-step-now-${idx + 1}`}
                      >
                        Send now
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Add Leads Modal */}
      <Dialog open={showAddLeadsModal} onOpenChange={setShowAddLeadsModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Leads to Campaign</DialogTitle>
            <DialogDescription className="text-gray-400">
              Select verified leads to add to this campaign
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {availableLeads.length > 0 ? (
              <div className="space-y-2">
                {availableLeads.map((lead) => (
                  <div 
                    key={lead.id}
                    onClick={() => {
                      if (selectedLeadIds.includes(lead.id)) {
                        setSelectedLeadIds(selectedLeadIds.filter(id => id !== lead.id));
                      } else {
                        setSelectedLeadIds([...selectedLeadIds, lead.id]);
                      }
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedLeadIds.includes(lead.id) 
                        ? 'bg-[#e6007e]/20 border border-[#e6007e]' 
                        : 'bg-gray-700/50 border border-transparent hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Checkbox checked={selectedLeadIds.includes(lead.id)} className="border-gray-500" />
                        <div>
                          <p className="text-white font-medium">{lead.first_name} {lead.last_name}</p>
                          <p className="text-gray-400 text-sm">{lead.email}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm">{lead.company_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                No available leads found. Import or verify leads first.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddLeadsModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddLeads} 
              disabled={selectedLeadIds.length === 0}
              className="bg-[#e6007e] hover:bg-[#c4006b]"
            >
              Add {selectedLeadIds.length} Lead{selectedLeadIds.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== REFERRALS PAGE ====================

export const OutreachReferralsPage = () => {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchReferrals();
  }, [adminToken, navigate]);

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/referrals`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setReferrals(response.data.referrals || []);
      setStats(response.data.stats || {});
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch referrals",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-gray-500',
      signed_up: 'bg-blue-500',
      converted: 'bg-green-500',
      expired: 'bg-red-500'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs text-white ${colors[status] || 'bg-gray-500'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {!new URLSearchParams(window.location.search).has('from') && (
        <>
          <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Send className="w-4 h-4 inline mr-2" />Campaigns
            </button>
            <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <FileText className="w-4 h-4 inline mr-2" />Templates
            </button>
            <button onClick={() => navigate('/admin/outreach/referrals')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <Share2 className="w-4 h-4 inline mr-2" />Referrals
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>
        </>
      )}
      {new URLSearchParams(window.location.search).has('from') && (
        <ReachEmbedNav active="referrals" />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Referral Tracking</h1>
            <p className="text-gray-400">Monitor word-of-mouth and referral activity</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Total Referrals</p>
              <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Signed Up</p>
              <p className="text-2xl font-bold text-blue-400">{stats.signed_up || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Converted</p>
              <p className="text-2xl font-bold text-green-400">{stats.converted || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Conversion Rate</p>
              <p className="text-2xl font-bold text-[#e6007e]">{stats.conversion_rate || 0}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Referrals Table */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Referral Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Referrer</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Code</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Source</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Referred Email</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((referral) => (
                  <tr key={referral.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="p-4 text-white">{referral.referrer_email}</td>
                    <td className="p-4">
                      <code className="bg-gray-700 px-2 py-1 rounded text-[#e6007e]">{referral.referral_code}</code>
                    </td>
                    <td className="p-4 text-gray-300">{referral.source}</td>
                    <td className="p-4 text-gray-300">{referral.referred_email || '-'}</td>
                    <td className="p-4">{getStatusBadge(referral.status)}</td>
                    <td className="p-4 text-gray-400 text-sm">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {referrals.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-400">
                      No referrals yet. They'll appear here when users share their referral links.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};


// ==================== QUICK SEND PAGE ====================

export const OutreachQuickSendPage = () => {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [email, setEmail] = useState({ to_email: '', to_name: '', subject: '', body_html: '' });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchLeads();
    fetchHistory();
    fetchAttachments();
    fetchSettings();
  }, [adminToken, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/global-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSettings(response.data.settings || {});
    } catch (err) {
      console.error('Failed to fetch settings', err);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/leads?limit=100`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setLeads(response.data.leads || []);
    } catch (err) {
      console.error('Failed to fetch leads', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/quick-send/history`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setHistory(response.data.emails || []);
    } catch (err) {
      console.error('Failed to fetch history', err);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/attachments`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSavedAttachments(response.data.attachments || []);
    } catch (err) {
      console.error('Failed to fetch attachments', err);
    }
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAttachment(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/attachments`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        toast({ title: "Success", description: "Attachment uploaded!" });
        fetchAttachments();
        // Auto-select the newly uploaded attachment
        setSelectedAttachments([...selectedAttachments, response.data.attachment.id]);
      }
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Failed to upload attachment", 
        variant: "destructive" 
      });
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`${API}/api/admin/outreach/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSavedAttachments(savedAttachments.filter(a => a.id !== attachmentId));
      setSelectedAttachments(selectedAttachments.filter(id => id !== attachmentId));
      toast({ title: "Success", description: "Attachment deleted" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to delete attachment", variant: "destructive" });
    }
  };

  const toggleAttachment = (attachmentId) => {
    if (selectedAttachments.includes(attachmentId)) {
      setSelectedAttachments(selectedAttachments.filter(id => id !== attachmentId));
    } else {
      setSelectedAttachments([...selectedAttachments, attachmentId]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setEmail({
      ...email,
      to_email: lead.email,
      to_name: lead.first_name || '',
      lead_id: lead.id
    });
  };

  const handleGenerateAI = async () => {
    if (!email.to_email) {
      toast({ title: "Error", description: "Enter recipient email first", variant: "destructive" });
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/quick-send/generate-ai`,
        null,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          params: {
            to_name: email.to_name || 'there',
            company_name: selectedLead?.company_name || 'your company',
            industry: selectedLead?.industry
          }
        }
      );
      
      const generated = response.data.generated;
      setEmail({
        ...email,
        subject: generated.subject,
        body_html: generated.body_html
      });
      toast({ title: "Success", description: "Email generated with AI" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to generate email", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!email.subject || !email.body_html) {
      toast({ title: "Error", description: "Enter subject and message first", variant: "destructive" });
      return;
    }
    
    setPreviewLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/preview-email`,
        {
          subject: email.subject,
          body_html: email.body_html,
          first_name: email.to_name || selectedLead?.first_name || "James",
          last_name: selectedLead?.last_name || "Wilson",
          company_name: selectedLead?.company_name || "Smith & Co",
          position: selectedLead?.position || "Managing Director",
          email: email.to_email || "example@company.co.uk"
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      setPreviewHtml(response.data.preview.full_preview);
      setShowPreview(true);
    } catch (err) {
      toast({ title: "Error", description: "Failed to generate preview", variant: "destructive" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!email.to_email || !email.subject || !email.body_html) {
      toast({ title: "Error", description: "Fill in all required fields", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/outreach/quick-send`, {
        to_email: email.to_email,
        to_name: email.to_name,
        subject: email.subject,
        body_html: email.body_html,
        lead_id: selectedLead?.id,
        attachment_ids: selectedAttachments
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      toast({ title: "Success", description: `Email sent${selectedAttachments.length > 0 ? ` with ${selectedAttachments.length} attachment(s)` : ''}!` });
      setEmail({ to_email: '', to_name: '', subject: '', body_html: '' });
      setSelectedLead(null);
      setSelectedAttachments([]);
      fetchHistory();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to send email", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/quick-send')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <Send className="w-4 h-4 inline mr-2" />Quick Send
            </button>
            <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Mail className="w-4 h-4 inline mr-2" />Campaigns
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Lead Selector */}
          <div>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Select Recipient</CardTitle>
                <CardDescription className="text-gray-400">Pick from leads or enter manually</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[400px] overflow-y-auto space-y-2">
                {leads.slice(0, 20).map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => handleSelectLead(lead)}
                    className={`p-3 rounded cursor-pointer transition-colors ${
                      selectedLead?.id === lead.id 
                        ? 'bg-[#e6007e]/20 border border-[#e6007e]' 
                        : 'bg-gray-700/50 hover:bg-gray-700'
                    }`}
                  >
                    <p className="text-white text-sm font-medium">{lead.email}</p>
                    <p className="text-gray-400 text-xs">{lead.company_name || lead.first_name || 'No name'}</p>
                  </div>
                ))}
                {leads.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-4">No leads yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Center: Email Composer */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white text-lg">Compose Email</CardTitle>
                    <CardDescription className="text-gray-400">Send a direct email without campaigns</CardDescription>
                  </div>
                  <Button onClick={handleGenerateAI} disabled={generating} variant="outline" className="border-[#e6007e] text-[#e6007e]">
                    {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate with AI
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white font-medium">To Email *</Label>
                    <Input
                      type="email"
                      value={email.to_email}
                      onChange={(e) => {
                        setEmail({...email, to_email: e.target.value});
                        // Clear selected lead if manually typing a different email
                        if (selectedLead && e.target.value !== selectedLead.email) {
                          setSelectedLead(null);
                        }
                      }}
                      placeholder="recipient@company.com"
                      className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                    />
                    {selectedLead && (
                      <p className="text-xs text-[#e6007e] mt-1">
                        Linked to lead: {selectedLead.company_name || selectedLead.first_name}
                        <button 
                          onClick={() => setSelectedLead(null)} 
                          className="ml-2 text-gray-400 hover:text-white underline"
                        >
                          (clear)
                        </button>
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-white font-medium">Recipient Name</Label>
                    <Input
                      value={email.to_name}
                      onChange={(e) => setEmail({...email, to_name: e.target.value})}
                      placeholder="John"
                      className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white font-medium">Subject *</Label>
                  <Input
                    value={email.subject}
                    onChange={(e) => setEmail({...email, subject: e.target.value})}
                    placeholder="Quick question about your business"
                    className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label className="text-white font-medium">Message *</Label>
                  <Textarea
                    value={email.body_html}
                    onChange={(e) => setEmail({...email, body_html: e.target.value})}
                    placeholder="Hi there,

I noticed your business and wanted to reach out..."
                    className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400 min-h-[200px]"
                  />
                  <p className="text-gray-400 text-xs mt-1">HTML supported. Tracking pixel and unsubscribe link added automatically.</p>
                </div>

                {/* Combined Signature & Logo Preview */}
                {(settings?.email?.signature_html || settings?.logo_url) && (
                  <div className="p-3 bg-gray-700/30 rounded-lg border border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-400 text-sm">Email Signature (added at bottom)</Label>
                      <span className="text-green-400 text-xs">✓ Auto-appended to all emails</span>
                    </div>
                    <div className="bg-gray-800/50 p-3 rounded space-y-2">
                      {settings?.email?.signature_html && (
                        <div 
                          className="text-gray-300 text-sm"
                          dangerouslySetInnerHTML={{ __html: settings.email.signature_html }}
                        />
                      )}
                      {settings?.logo_url && (
                        <img 
                          src={settings.logo_url} 
                          alt="Email Logo" 
                          className="max-h-12 max-w-[150px] object-contain mt-2"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Attachments Section */}
                <div className="p-4 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-white font-medium">Attachments</Label>
                    <div>
                      <input
                        type="file"
                        id="attachment-upload"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt,.csv"
                        className="hidden"
                        onChange={handleUploadAttachment}
                      />
                      <label htmlFor="attachment-upload">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-[#e6007e] text-[#e6007e] cursor-pointer"
                          disabled={uploadingAttachment}
                          asChild
                        >
                          <span>
                            {uploadingAttachment ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                            Upload New
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                  
                  {savedAttachments.length > 0 ? (
                    <div className="space-y-2 max-h-[150px] overflow-y-auto">
                      {savedAttachments.map((att) => (
                        <div 
                          key={att.id}
                          onClick={() => toggleAttachment(att.id)}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            selectedAttachments.includes(att.id)
                              ? 'bg-[#e6007e]/20 border border-[#e6007e]'
                              : 'bg-gray-600/50 hover:bg-gray-600'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              checked={selectedAttachments.includes(att.id)}
                              className="border-gray-400"
                            />
                            <FileText className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-white text-sm">{att.filename}</p>
                              <p className="text-gray-400 text-xs">{formatFileSize(att.size)}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAttachment(att.id);
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-7 w-7 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">
                      No saved attachments. Upload PDFs, documents, or images to attach to emails.
                    </p>
                  )}
                  
                  {selectedAttachments.length > 0 && (
                    <p className="text-[#e6007e] text-xs mt-2">
                      {selectedAttachments.length} attachment(s) selected
                    </p>
                  )}
                </div>

                <div className="flex space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setEmail({ to_email: '', to_name: '', subject: '', body_html: '' });
                      setSelectedLead(null);
                      setSelectedAttachments([]);
                    }} 
                    className="border-gray-500 text-gray-300 hover:bg-gray-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handlePreview} 
                    disabled={previewLoading || !email.subject || !email.body_html}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {previewLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                    Preview
                  </Button>
                  <Button onClick={handleSend} disabled={loading} className="flex-1 bg-[#e6007e] hover:bg-[#c4006b]">
                    {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Email
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent History */}
            {history.length > 0 && (
              <Card className="bg-gray-800 border-gray-700 mt-6">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Recent Sent</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {history.slice(0, 5).map((sentEmail) => (
                      <div key={sentEmail.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded">
                        <div>
                          <p className="text-white text-sm">{sentEmail.to_email}</p>
                          <p className="text-gray-400 text-xs">{sentEmail.subject}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded ${sentEmail.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {sentEmail.status}
                          </span>
                          <p className="text-gray-500 text-xs mt-1">{new Date(sentEmail.sent_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Email Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Eye className="w-5 h-5 mr-2 text-blue-400" />
              Email Preview
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This is how your email will appear to the recipient
            </DialogDescription>
          </DialogHeader>
          <div 
            className="mt-4 bg-white rounded-lg"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setShowPreview(false)}>Close</Button>
            <Button onClick={() => { setShowPreview(false); handleSend(); }} className="bg-[#e6007e] hover:bg-[#c4006b]">
              <Send className="w-4 h-4 mr-2" />Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== AUTOMATION PAGE (SECTOR QUEUE) ====================

export const OutreachAutomationPage = () => {
  const [sectors, setSectors] = useState([]);
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [previewLeads, setPreviewLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewSector, setPreviewSector] = useState('');
  const [newSector, setNewSector] = useState('');
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [adminToken, navigate]);

  const fetchData = async () => {
    try {
      const [sectorsRes, settingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/api/admin/outreach/sectors`, { headers: { Authorization: `Bearer ${adminToken}` }}),
        axios.get(`${API}/api/admin/outreach/settings`, { headers: { Authorization: `Bearer ${adminToken}` }}),
        axios.get(`${API}/api/admin/outreach/templates`, { headers: { Authorization: `Bearer ${adminToken}` }})
      ]);
      setSectors(sectorsRes.data.sectors || []);
      setSettings(settingsRes.data.settings || {});
      setTemplates(templatesRes.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSector = async () => {
    if (!newSector.trim()) return;
    
    try {
      await axios.post(`${API}/api/admin/outreach/sectors`, {
        query: newSector.trim(),
        seniority: ["executive", "senior"],
        departments: ["executive", "management"]
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: `Added "${newSector}" to queue` });
      setNewSector('');
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: "Failed to add sector", variant: "destructive" });
    }
  };

  const handleDeleteSector = async (sectorId) => {
    try {
      await axios.delete(`${API}/api/admin/outreach/sectors/${sectorId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Deleted", description: "Sector removed" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: "Failed to remove sector", variant: "destructive" });
    }
  };

  const handleClearAllSectors = async () => {
    try {
      await axios.delete(`${API}/api/admin/outreach/sectors/clear-all`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Cleared", description: "All sectors removed from queue" });
      setShowClearConfirm(false);
      setShowPreview(false);
      setPreviewLeads([]);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: "Failed to clear queue", variant: "destructive" });
    }
  };

  const handleSkipToNextSector = async () => {
    // Find the current in_progress sector and mark it complete, activate next
    const currentSector = sectors.find(s => s.status === 'in_progress');
    if (currentSector) {
      try {
        await axios.post(`${API}/api/admin/outreach/sectors/${currentSector.id}/complete`, {}, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        toast({ title: "Skipped", description: `Moved to next sector` });
        setShowPreview(false);
        setPreviewLeads([]);
        fetchData();
      } catch (err) {
        toast({ title: "Error", description: "Failed to skip sector", variant: "destructive" });
      }
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const response = await axios.post(`${API}/api/admin/outreach/run`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      const data = response.data;
      if (data.limit_reached) {
        toast({ title: "Limit Reached", description: "Daily email limit reached. Try again tomorrow.", variant: "destructive" });
      } else {
        toast({ 
          title: "Success", 
          description: `${data.emails_sent} emails sent, ${data.leads_created} new leads from "${data.sector || 'queue'}"` 
        });
      }
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to run", variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  const handlePreviewLeads = async () => {
    setPreviewing(true);
    setShowPreview(false);
    setPreviewLeads([]);
    setSelectedLeads([]);
    
    try {
      const response = await axios.post(`${API}/api/admin/outreach/preview-leads`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      const data = response.data;
      if (data.leads && data.leads.length > 0) {
        setPreviewLeads(data.leads);
        // Only auto-select leads that haven't been contacted
        setSelectedLeads(data.leads.filter(l => !l.already_contacted).map(l => l.email));
        setPreviewSector(data.sector);
        setShowPreview(true);
        
        const newLeads = data.leads.filter(l => !l.already_contacted).length;
        const contacted = data.leads.filter(l => l.already_contacted).length;
        toast({ 
          title: "Preview Ready", 
          description: `Found ${newLeads} new leads${contacted > 0 ? ` (${contacted} already contacted)` : ''}` 
        });
      } else {
        toast({ title: "No Leads Found", description: data.message || "No more leads in this sector", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to preview", variant: "destructive" });
    } finally {
      setPreviewing(false);
    }
  };

  const handleApproveAndSend = async () => {
    if (selectedLeads.length === 0) {
      toast({ title: "Error", description: "Select at least one lead", variant: "destructive" });
      return;
    }
    
    // Show confirmation
    setShowConfirmSend(true);
  };

  const confirmAndSend = async () => {
    setShowConfirmSend(false);
    setRunning(true);
    try {
      const leadsToSend = previewLeads.filter(l => selectedLeads.includes(l.email));
      const response = await axios.post(`${API}/api/admin/outreach/approve-and-send`, leadsToSend, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      const data = response.data;
      toast({ title: "✓ Sent!", description: data.message });
      setShowPreview(false);
      setPreviewLeads([]);
      setSelectedLeads([]);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to send", variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/api/admin/outreach/cancel`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Cancelled", description: response.data.message });
      setShowPreview(false);
      setPreviewLeads([]);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: "Failed to cancel", variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  };

  const toggleLeadSelection = (email) => {
    if (selectedLeads.includes(email)) {
      setSelectedLeads(selectedLeads.filter(e => e !== email));
    } else {
      setSelectedLeads([...selectedLeads, email]);
    }
  };

  const handleSaveSettings = async (templateId, dailyLimit) => {
    try {
      await axios.post(`${API}/api/admin/outreach/settings`, null, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: {
          enabled: true,
          daily_email_limit: dailyLimit,
          template_id: templateId
        }
      });
      toast({ title: "Success", description: "Settings saved" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    const colors = {
      queued: 'bg-gray-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      paused: 'bg-yellow-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/quick-send')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Send className="w-4 h-4 inline mr-2" />Quick Send
            </button>
            <button onClick={() => navigate('/admin/outreach/automation')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <Zap className="w-4 h-4 inline mr-2" />Automation
            </button>
            <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <FileText className="w-4 h-4 inline mr-2" />Templates
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header with Instructions */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Sector Automation</h1>
              <p className="text-gray-400">Find and email decision makers in any UK industry</p>
            </div>
            <div className="flex space-x-3">
              <Button 
                onClick={handleCancel} 
                disabled={cancelling}
                variant="outline"
                size="sm"
                className="border-red-500 text-red-400 hover:bg-red-500/10"
              >
                {cancelling ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                Cancel All
              </Button>
            </div>
          </div>
          
          {/* Step-by-step Instructions */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-8 text-sm">
              <div className="flex items-center space-x-2">
                <span className="w-6 h-6 rounded-full bg-[#e6007e] text-white flex items-center justify-center text-xs font-bold">1</span>
                <span className="text-gray-300">Add a sector (e.g. "plumbers london")</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
              <div className="flex items-center space-x-2">
                <span className="w-6 h-6 rounded-full bg-[#e6007e] text-white flex items-center justify-center text-xs font-bold">2</span>
                <span className="text-gray-300">Select email template</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
              <div className="flex items-center space-x-2">
                <span className="w-6 h-6 rounded-full bg-[#e6007e] text-white flex items-center justify-center text-xs font-bold">3</span>
                <span className="text-gray-300">Click "Find Leads" to preview</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
              <div className="flex items-center space-x-2">
                <span className="w-6 h-6 rounded-full bg-[#e6007e] text-white flex items-center justify-center text-xs font-bold">4</span>
                <span className="text-gray-300">Review & Send</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Action Button */}
        <div className="flex justify-center mb-6">
          <Button 
            onClick={handlePreviewLeads} 
            disabled={previewing || sectors.filter(s => s.status === 'queued' || s.status === 'in_progress').length === 0 || !settings?.template_id}
            size="lg"
            className="bg-[#e6007e] hover:bg-[#c4006b] text-lg px-12 py-6"
          >
            {previewing ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Finding Leads...
              </>
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Find Leads & Preview
              </>
            )}
          </Button>
        </div>

        {/* Lead Preview Panel */}
        {showPreview && (
          <Card className="bg-gray-800 border-[#e6007e] mb-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center">
                    <Target className="w-5 h-5 mr-2 text-[#e6007e]" />
                    {previewSector}
                  </CardTitle>
                  {previewLeads.filter(l => !l.already_contacted).length > 0 ? (
                    <CardDescription className="text-green-400 mt-1">
                      ✓ {previewLeads.filter(l => !l.already_contacted).length} new leads ready to email
                    </CardDescription>
                  ) : (
                    <CardDescription className="text-yellow-400 mt-1">
                      All {previewLeads.length} leads already contacted
                    </CardDescription>
                  )}
                </div>
                <div className="flex space-x-2">
                  {previewLeads.filter(l => !l.already_contacted).length > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedLeads(previewLeads.filter(l => !l.already_contacted).map(l => l.email))}
                        className="text-green-400 border-green-500/50 hover:bg-green-500/10"
                      >
                        Select New Only
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedLeads([])}
                        className="text-gray-400"
                      >
                        Deselect All
                      </Button>
                    </>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSkipToNextSector}
                    className="text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/10"
                  >
                    Skip to Next →
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Empty State - All leads contacted */}
              {previewLeads.length > 0 && previewLeads.filter(l => !l.already_contacted).length === 0 && (
                <div className="mb-4 p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                  <CheckCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <h3 className="text-yellow-400 font-semibold mb-2">Sector Complete!</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    All {previewLeads.length} leads in "{previewSector}" have already been contacted.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <Button 
                      onClick={handleSkipToNextSector}
                      className="bg-yellow-500 hover:bg-yellow-600 text-gray-900"
                    >
                      <ChevronRight className="w-4 h-4 mr-1" />
                      Move to Next Sector
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowPreview(false);
                        setPreviewLeads([]);
                      }}
                      className="border-gray-600 text-gray-400"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
              
              {/* New Leads Section */}
              {previewLeads.filter(l => !l.already_contacted).length > 0 && (
                <div className="mb-4">
                  <p className="text-green-400 text-sm font-medium mb-2">✓ New Leads ({previewLeads.filter(l => !l.already_contacted).length})</p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {previewLeads.filter(l => !l.already_contacted).map((lead, i) => (
                      <div 
                        key={i}
                        onClick={() => toggleLeadSelection(lead.email)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedLeads.includes(lead.email) 
                            ? 'bg-[#e6007e]/20 border border-[#e6007e]' 
                            : 'bg-gray-700/50 border border-transparent hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Checkbox checked={selectedLeads.includes(lead.email)} className="border-gray-500" />
                            <div>
                              <p className="text-white font-medium">
                                {lead.first_name} {lead.last_name}
                                {lead.position && <span className="text-gray-400 font-normal ml-2">• {lead.position}</span>}
                              </p>
                              <p className="text-gray-400 text-sm">{lead.email}</p>
                            </div>
                          </div>
                          <p className="text-gray-300 text-sm">{lead.company_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Already Contacted Section - only show if there are also new leads */}
              {previewLeads.filter(l => l.already_contacted).length > 0 && previewLeads.filter(l => !l.already_contacted).length > 0 && (
                <div className="mb-4">
                  <p className="text-gray-500 text-sm font-medium mb-2">Already Contacted ({previewLeads.filter(l => l.already_contacted).length})</p>
                  <div className="space-y-1 max-h-[100px] overflow-y-auto opacity-50">
                    {previewLeads.filter(l => l.already_contacted).map((lead, i) => (
                      <div key={i} className="p-2 rounded bg-gray-700/30 text-sm">
                        <span className="text-gray-400">{lead.first_name} {lead.last_name}</span>
                        <span className="text-gray-500 ml-2">• {lead.company_name}</span>
                        <CheckCircle className="w-3 h-3 text-green-500 inline ml-2" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons - only show if there are new leads */}
              {previewLeads.filter(l => !l.already_contacted).length > 0 && (
              <div className="flex space-x-3 pt-3 border-t border-gray-700">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewLeads([]);
                    setSelectedLeads([]);
                  }}
                  className="border-gray-600 text-gray-400"
                >
                  Close
                </Button>
                <Button 
                  onClick={handleApproveAndSend}
                  disabled={running || selectedLeads.length === 0}
                  className="flex-1 bg-[#e6007e] hover:bg-[#c4006b]"
                >
                  {running ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Send to {selectedLeads.length} Lead{selectedLeads.length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Confirmation Modal */}
        {showConfirmSend && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="bg-gray-800 border-[#e6007e] w-[400px]">
              <CardHeader>
                <CardTitle className="text-white">Confirm Send</CardTitle>
                <CardDescription className="text-gray-400">
                  You are about to send emails to {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-700/50 rounded p-3 mb-4">
                  <p className="text-gray-300 text-sm">Template: <span className="text-white">{templates.find(t => t.id === settings?.template_id)?.name || 'Selected template'}</span></p>
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setShowConfirmSend(false)} className="flex-1 border-gray-600 text-gray-400">
                    Cancel
                  </Button>
                  <Button onClick={confirmAndSend} className="flex-1 bg-[#e6007e] hover:bg-[#c4006b]">
                    <Send className="w-4 h-4 mr-2" />
                    Confirm & Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Clear All Confirmation */}
        {showClearConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="bg-gray-800 border-red-500 w-[400px]">
              <CardHeader>
                <CardTitle className="text-white">Clear All Sectors?</CardTitle>
                <CardDescription className="text-gray-400">
                  This will remove all {sectors.length} sectors from the queue. This cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setShowClearConfirm(false)} className="flex-1 border-gray-600 text-gray-400">
                    Cancel
                  </Button>
                  <Button onClick={handleClearAllSectors} className="flex-1 bg-red-500 hover:bg-red-600">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sector Queue */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Sector Queue</CardTitle>
                <CardDescription className="text-gray-400">
                  Add sectors like "plumbers uk", "electricians london", "web designers manchester"
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Add Sector Input */}
                <div className="flex space-x-3 mb-4">
                  <Input
                    value={newSector}
                    onChange={(e) => setNewSector(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSector()}
                    placeholder="e.g., plumbers london"
                    className="bg-gray-700 border-gray-600"
                  />
                  <Button onClick={handleAddSector} className="bg-[#e6007e] hover:bg-[#c4006b] px-6">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>

                {/* Clear All Button */}
                {sectors.length > 0 && (
                  <div className="flex justify-end mb-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowClearConfirm(true)}
                      className="text-red-400 border-red-500/50 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All ({sectors.length})
                    </Button>
                  </div>
                )}

                {/* Queue List */}
                <div className="space-y-2">
                  {sectors.map((sector, index) => (
                    <div 
                      key={sector.id} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        sector.status === 'in_progress' ? 'bg-[#e6007e]/20 border border-[#e6007e]' : 
                        sector.status === 'completed' ? 'bg-green-500/10 border border-green-500/50' :
                        'bg-gray-700/50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-500 text-xs w-5">{index + 1}</span>
                        <div>
                          <p className="text-white font-medium">{sector.query}</p>
                          <div className="flex items-center space-x-3 text-xs">
                            <span className={sector.domains_found > 0 ? 'text-green-400' : 'text-gray-500'}>
                              {sector.domains_found || 0} leads
                            </span>
                            <span className={sector.emails_sent > 0 ? 'text-[#e6007e]' : 'text-gray-500'}>
                              {sector.emails_sent || 0} sent
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {sector.status === 'in_progress' && (
                          <span className="px-2 py-0.5 rounded text-xs bg-[#e6007e] text-white">Active</span>
                        )}
                        {sector.status === 'completed' && (
                          <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">Done</span>
                        )}
                        {sector.status === 'queued' && (
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-600 text-gray-300">Queued</span>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDeleteSector(sector.id)}
                          className="text-gray-500 hover:text-red-400 h-7 w-7 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {sectors.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No sectors yet</p>
                      <p className="text-xs">Add one above to start</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar - Settings & Quick Guide */}
          <div>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Outreach Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-400">Email Template *</Label>
                  <Select 
                    value={settings?.template_id || "none"}
                    onValueChange={(v) => handleSaveSettings(v === "none" ? null : v, settings?.daily_email_limit || 50)}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600">
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a template</SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!settings?.template_id && (
                    <p className="text-orange-400 text-xs mt-1">Required before running</p>
                  )}
                </div>

                <div>
                  <Label className="text-gray-400">Daily Email Limit</Label>
                  <Input
                    type="number"
                    value={settings?.daily_email_limit || 50}
                    onChange={(e) => handleSaveSettings(settings?.template_id, parseInt(e.target.value) || 50)}
                    min="1"
                    max="200"
                    className="bg-gray-700 border-gray-600"
                  />
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <p className="text-gray-400 text-sm mb-2">Today's Progress</p>
                  <div className="bg-gray-700/50 p-3 rounded">
                    <p className="text-2xl font-bold text-[#e6007e]">{settings?.emails_sent_today || 0}</p>
                    <p className="text-gray-400 text-sm">/ {settings?.daily_email_limit || 50} emails</p>
                  </div>
                </div>

                {templates.length === 0 && (
                  <div className="bg-orange-500/20 border border-orange-500 rounded p-3">
                    <p className="text-orange-400 text-sm">
                      No templates found. <br/>
                      <button 
                        onClick={() => navigate('/admin/outreach/templates')} 
                        className="underline hover:text-orange-300"
                      >
                        Create a template first →
                      </button>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* How it works */}
            <Card className="bg-gray-800 border-gray-700 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Quick Guide</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <div className="flex items-start space-x-2">
                  <span className="text-[#e6007e] font-bold">1.</span>
                  <span className="text-gray-300">Type a sector + location (e.g. "plumbers london", "accountants manchester")</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-[#e6007e] font-bold">2.</span>
                  <span className="text-gray-300">Choose your email template above</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-[#e6007e] font-bold">3.</span>
                  <span className="text-gray-300">Click the big pink <strong>"Find Leads & Preview"</strong> button</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-[#e6007e] font-bold">4.</span>
                  <span className="text-gray-300">Review the leads found - tick/untick who to email</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-[#e6007e] font-bold">5.</span>
                  <span className="text-gray-300">Click <strong>"Send"</strong> - emails go only to selected leads</span>
                </div>
                <div className="mt-4 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-yellow-400 text-xs">
                  ⚠️ Always preview leads before sending - you control exactly who gets emailed!
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};


// ==================== SETTINGS PAGE ====================

export const OutreachSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [checkingDns, setCheckingDns] = useState(false);
  const [dnsResults, setDnsResults] = useState(null);
  const [dnsDomain, setDnsDomain] = useState('');
  const [discoveryStatus, setDiscoveryStatus] = useState(null);
  const [apolloKey, setApolloKey] = useState('');
  const [chKey, setChKey] = useState('');
  const [savingDiscoveryKeys, setSavingDiscoveryKeys] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  const fetchDiscoveryStatus = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/reach/discover/sectors`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setDiscoveryStatus(r.data);
    } catch (_) {
      // Non-fatal — card still shows, just without the green pills
    }
  };

  const saveDiscoveryKeys = async () => {
    if (!apolloKey && !chKey) {
      toast({ title: 'Nothing to save', description: 'Enter at least one key.', variant: 'destructive' });
      return;
    }
    setSavingDiscoveryKeys(true);
    try {
      const payload = {};
      if (apolloKey) payload.apollo_api_key = apolloKey;
      if (chKey) payload.companies_house_api_key = chKey;
      await axios.post(`${API}/api/admin/reach/discover/keys`, payload, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast({
        title: 'Discovery keys saved',
        description: 'Head to Reach → Folders → Discover leads to run the agent.',
      });
      setApolloKey('');
      setChKey('');
      fetchDiscoveryStatus();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSavingDiscoveryKeys(false);
    }
  };

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchSettings();
    fetchDiscoveryStatus();
  }, [adminToken, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/global-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSettings(response.data.settings);
    } catch (err) {
      console.error('Failed to fetch settings', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/outreach/global-settings`, settings, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: "Success", description: "Settings saved" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      // Save first
      await axios.post(`${API}/api/admin/outreach/global-settings`, settings, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      const response = await axios.post(`${API}/api/admin/outreach/global-settings/test-smtp`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      if (response.data.success) {
        toast({ title: "Success", description: "SMTP connection successful!" });
        setSettings({...settings, email: {...settings.email, smtp_verified: true}});
      } else {
        toast({ title: "Error", description: response.data.message, variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.message || "SMTP test failed", variant: "destructive" });
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestImap = async () => {
    setTestingImap(true);
    try {
      // Save first
      await axios.post(`${API}/api/admin/outreach/global-settings`, settings, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      const response = await axios.post(`${API}/api/admin/outreach/global-settings/test-imap`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      if (response.data.success) {
        toast({ title: "Success", description: "IMAP connection successful!" });
        setSettings({...settings, email: {...settings.email, imap_verified: true}});
      } else {
        toast({ title: "Error", description: response.data.message, variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.message || "IMAP test failed", variant: "destructive" });
    } finally {
      setTestingImap(false);
    }
  };

  const handleCheckDns = async () => {
    if (!dnsDomain) {
      // Try to extract domain from SMTP email
      const emailDomain = settings?.email?.smtp_username?.split('@')[1] || '';
      if (emailDomain) {
        setDnsDomain(emailDomain);
      } else {
        toast({ title: "Error", description: "Please enter a domain to check", variant: "destructive" });
        return;
      }
    }
    
    const domainToCheck = dnsDomain || settings?.email?.smtp_username?.split('@')[1];
    if (!domainToCheck) {
      toast({ title: "Error", description: "Please enter a domain", variant: "destructive" });
      return;
    }
    
    setCheckingDns(true);
    setDnsResults(null);
    
    try {
      const response = await axios.post(
        `${API}/api/admin/outreach/global-settings/check-dns?domain=${encodeURIComponent(domainToCheck)}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setDnsResults(response.data);
    } catch (err) {
      toast({ title: "Error", description: "Failed to check DNS records", variant: "destructive" });
    } finally {
      setCheckingDns(false);
    }
  };

  const updateEmail = (field, value) => {
    setSettings({
      ...settings,
      email: { ...settings.email, [field]: value }
    });
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {!new URLSearchParams(window.location.search).has('from') && (
        <>
          <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <Button onClick={handleSave} disabled={saving} className="bg-[#e6007e] hover:bg-[#c4006b]">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/pipeline')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <TrendingUp className="w-4 h-4 inline mr-2" />Pipeline
            </button>
            <button onClick={() => navigate('/admin/outreach/settings')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <Settings className="w-4 h-4 inline mr-2" />Settings
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>
        </>
      )}
      {new URLSearchParams(window.location.search).has('from') && (
        <ReachEmbedNav active="settings" />
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-8">Outreach Settings</h1>

        {/* Email Configuration */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Mail className="w-5 h-5 mr-2 text-[#e6007e]" />
              Email Configuration (Hostinger)
            </CardTitle>
            <CardDescription className="text-gray-400">Configure your email for sending outreach and detecting replies</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* SMTP Settings */}
            <div className="p-4 bg-gray-700/30 rounded-lg">
              <h3 className="text-white font-medium mb-4 flex items-center">
                <Send className="w-4 h-4 mr-2" />
                SMTP (Sending Emails)
                {settings.email?.smtp_verified && (
                  <span className="ml-2 text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Verified</span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">SMTP Host</Label>
                  <Input
                    value={settings.email?.smtp_host || "smtp.hostinger.com"}
                    onChange={(e) => updateEmail('smtp_host', e.target.value)}
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Port</Label>
                  <Input
                    type="number"
                    value={settings.email?.smtp_port || 587}
                    onChange={(e) => updateEmail('smtp_port', parseInt(e.target.value))}
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Email Address</Label>
                  <Input
                    type="email"
                    value={settings.email?.smtp_username || ""}
                    onChange={(e) => updateEmail('smtp_username', e.target.value)}
                    placeholder="you@yourdomain.com"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Password</Label>
                  <Input
                    type="password"
                    value={settings.email?.smtp_password || ""}
                    onChange={(e) => updateEmail('smtp_password', e.target.value)}
                    placeholder="••••••••"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
              </div>
              <Button onClick={handleTestSmtp} disabled={testingSmtp} className="mt-4" variant="outline">
                {testingSmtp ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Test SMTP Connection
              </Button>
            </div>

            {/* IMAP Settings */}
            <div className="p-4 bg-gray-700/30 rounded-lg">
              <h3 className="text-white font-medium mb-4 flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                IMAP (Reply Detection)
                {settings.email?.imap_verified && (
                  <span className="ml-2 text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Verified</span>
                )}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">IMAP Host</Label>
                  <Input
                    value={settings.email?.imap_host || "imap.hostinger.com"}
                    onChange={(e) => updateEmail('imap_host', e.target.value)}
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Port</Label>
                  <Input
                    type="number"
                    value={settings.email?.imap_port || 993}
                    onChange={(e) => updateEmail('imap_port', parseInt(e.target.value))}
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Email Address</Label>
                  <Input
                    type="email"
                    value={settings.email?.imap_username || ""}
                    onChange={(e) => updateEmail('imap_username', e.target.value)}
                    placeholder="you@yourdomain.com"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Password</Label>
                  <Input
                    type="password"
                    value={settings.email?.imap_password || ""}
                    onChange={(e) => updateEmail('imap_password', e.target.value)}
                    placeholder="••••••••"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
              </div>
              <Button onClick={handleTestImap} disabled={testingImap} className="mt-4" variant="outline">
                {testingImap ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Test IMAP Connection
              </Button>
            </div>

            {/* DNS Deliverability Check */}
            <div className="p-4 bg-gray-700/30 rounded-lg border border-yellow-500/30">
              <h3 className="text-white font-medium mb-2 flex items-center">
                <Globe className="w-4 h-4 mr-2 text-yellow-400" />
                DNS Deliverability Check
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Check if your domain has the correct DNS records (SPF, DKIM, DMARC) to ensure emails land in inbox, not spam.
              </p>
              
              <div className="flex space-x-2 mb-4">
                <Input
                  value={dnsDomain || settings?.email?.smtp_username?.split('@')[1] || ''}
                  onChange={(e) => setDnsDomain(e.target.value)}
                  placeholder="yourdomain.com"
                  className="bg-white text-gray-900 border-gray-300 max-w-[300px]"
                />
                <Button onClick={handleCheckDns} disabled={checkingDns} variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10">
                  {checkingDns ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Check DNS
                </Button>
              </div>
              
              {dnsResults && (
                <div className="space-y-3 mt-4">
                  {/* Overall Score */}
                  <div className={`p-3 rounded-lg ${
                    dnsResults.overall_score >= 80 ? 'bg-green-500/20 border border-green-500/50' :
                    dnsResults.overall_score >= 50 ? 'bg-yellow-500/20 border border-yellow-500/50' :
                    'bg-red-500/20 border border-red-500/50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">Deliverability Score</span>
                      <span className={`text-2xl font-bold ${
                        dnsResults.overall_score >= 80 ? 'text-green-400' :
                        dnsResults.overall_score >= 50 ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>{dnsResults.overall_score}/100</span>
                    </div>
                  </div>
                  
                  {/* Individual Records */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* SPF */}
                    <div className={`p-3 rounded-lg ${dnsResults.spf.status === 'found' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">SPF</span>
                        {dnsResults.spf.status === 'found' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <p className="text-gray-400 text-xs">{dnsResults.spf.message}</p>
                      {dnsResults.spf.record && (
                        <p className="text-gray-500 text-xs mt-1 truncate" title={dnsResults.spf.record}>{dnsResults.spf.record}</p>
                      )}
                    </div>
                    
                    {/* DMARC */}
                    <div className={`p-3 rounded-lg ${dnsResults.dmarc.status === 'found' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">DMARC</span>
                        {dnsResults.dmarc.status === 'found' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <p className="text-gray-400 text-xs">{dnsResults.dmarc.message}</p>
                    </div>
                    
                    {/* DKIM */}
                    <div className={`p-3 rounded-lg ${dnsResults.dkim.status === 'found' ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">DKIM</span>
                        {dnsResults.dkim.status === 'found' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-yellow-400" />
                        )}
                      </div>
                      <p className="text-gray-400 text-xs">{dnsResults.dkim.message}</p>
                    </div>
                    
                    {/* MX */}
                    <div className={`p-3 rounded-lg ${dnsResults.mx.status === 'found' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">MX Records</span>
                        {dnsResults.mx.status === 'found' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <p className="text-gray-400 text-xs">{dnsResults.mx.message}</p>
                    </div>
                  </div>
                  
                  {/* Recommendations */}
                  {dnsResults.recommendations && dnsResults.recommendations.length > 0 && (
                    <div className="p-3 bg-gray-800 rounded-lg mt-3">
                      <h4 className="text-white font-medium mb-2">Recommendations</h4>
                      <ul className="space-y-1">
                        {dnsResults.recommendations.map((rec, i) => (
                          <li key={i} className="text-gray-300 text-sm flex items-start">
                            <span className="mr-2">•</span>
                            <span className="font-mono text-xs break-all">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sender Info */}
            <div className="p-4 bg-gray-700/30 rounded-lg">
              <h3 className="text-white font-medium mb-4">Sender Info</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">From Name</Label>
                  <Input
                    value={settings.email?.from_name || ""}
                    onChange={(e) => updateEmail('from_name', e.target.value)}
                    placeholder="Your Name"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">From Email</Label>
                  <Input
                    type="email"
                    value={settings.email?.from_email || ""}
                    onChange={(e) => updateEmail('from_email', e.target.value)}
                    placeholder="you@yourdomain.com"
                    className="bg-white text-gray-900 border-gray-300"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-gray-400 mb-2 block">Email Signature</Label>
                <SignatureEditor
                  value={settings.email?.signature_html || ""}
                  onChange={(html) => updateEmail('signature_html', html)}
                  onSave={handleSave}
                  saving={saving}
                  logoUrl={settings.logo_url || ""}
                  senderName={settings.email?.from_name || ""}
                  senderEmail={settings.email?.from_email || settings.email?.smtp_username || ""}
                />
              </div>
            </div>

            {/* Logo Upload */}
            <div className="p-4 bg-gray-700/30 rounded-lg">
              <h3 className="text-white font-medium mb-4">Email Logo</h3>
              <p className="text-gray-400 text-sm mb-4">Upload a logo to appear in your outreach emails</p>
              
              {settings.logo_url ? (
                <div className="flex items-center space-x-4">
                  <div className="bg-white p-3 rounded-lg">
                    <img 
                      src={settings.logo_url} 
                      alt="Email Logo" 
                      className="max-h-16 max-w-[200px] object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-green-400 text-sm">✓ Logo uploaded</p>
                    {settings.logo_filename && (
                      <p className="text-gray-500 text-xs">{settings.logo_filename}</p>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/api/admin/outreach/global-settings/logo`, {
                          headers: { Authorization: `Bearer ${adminToken}` }
                        });
                        setSettings({...settings, logo_url: null, logo_filename: null});
                        toast({ title: "Success", description: "Logo deleted" });
                      } catch (err) {
                        toast({ title: "Error", description: "Failed to delete logo", variant: "destructive" });
                      }
                    }}
                    className="text-red-400 border-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const response = await axios.post(
                          `${API}/api/admin/outreach/global-settings/upload-logo`,
                          formData,
                          {
                            headers: { 
                              Authorization: `Bearer ${adminToken}`,
                              'Content-Type': 'multipart/form-data'
                            }
                          }
                        );
                        
                        if (response.data.success) {
                          setSettings({
                            ...settings, 
                            logo_url: response.data.logo_url,
                            logo_filename: response.data.filename
                          });
                          toast({ title: "Success", description: "Logo uploaded successfully!" });
                        }
                      } catch (err) {
                        toast({ 
                          title: "Error", 
                          description: err.response?.data?.detail || "Failed to upload logo", 
                          variant: "destructive" 
                        });
                      }
                      
                      // Reset input
                      e.target.value = '';
                    }}
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400">Click to upload logo</p>
                    <p className="text-gray-500 text-xs mt-1">PNG, JPG, GIF, or WebP (max 2MB)</p>
                  </label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lead Discovery (Apollo + Companies House) */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-[#e6007e]" />
              Lead Discovery agent
            </CardTitle>
            <CardDescription className="text-gray-400">
              Auto-find UK SME decision-makers by sector and region. Keys are also accessible from{' '}
              <button
                type="button"
                onClick={() => navigate('/admin/reach/settings')}
                className="text-[#e6007e] hover:underline"
              >
                Reach → Settings
              </button>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {discoveryStatus && (
              <div className="flex flex-wrap gap-2 text-xs" data-testid="outreach-discovery-status">
                {discoveryStatus.apollo_configured ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                    <CheckCircle className="w-3.5 h-3.5" />Apollo connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/30">
                    <AlertTriangle className="w-3.5 h-3.5" />Apollo key missing
                  </span>
                )}
                {discoveryStatus.companies_house_configured ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                    <CheckCircle className="w-3.5 h-3.5" />Companies House connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-500/30">
                    <AlertTriangle className="w-3.5 h-3.5" />Companies House key missing
                  </span>
                )}
              </div>
            )}

            <div>
              <Label className="text-gray-400">
                Apollo.io API key{' '}
                <a href="https://app.apollo.io" target="_blank" rel="noreferrer" className="text-[#e6007e] hover:underline text-xs ml-1">
                  (Settings → Integrations → API)
                </a>
              </Label>
              <Input
                type="password"
                value={apolloKey}
                onChange={(e) => setApolloKey(e.target.value)}
                placeholder={discoveryStatus?.apollo_configured ? '•••••••• (already set — paste a new key to replace)' : 'Paste your Apollo API key'}
                className="bg-white text-gray-900 border-gray-300 mt-1"
                data-testid="outreach-apollo-key-input"
              />
            </div>

            <div>
              <Label className="text-gray-400">
                Companies House API key{' '}
                <a href="https://developer.company-information.service.gov.uk/" target="_blank" rel="noreferrer" className="text-[#e6007e] hover:underline text-xs ml-1">
                  (free, UK gov)
                </a>
              </Label>
              <Input
                type="password"
                value={chKey}
                onChange={(e) => setChKey(e.target.value)}
                placeholder={discoveryStatus?.companies_house_configured ? '•••••••• (already set — paste a new key to replace)' : 'Paste your Companies House API key'}
                className="bg-white text-gray-900 border-gray-300 mt-1"
                data-testid="outreach-ch-key-input"
              />
            </div>

            <Button
              onClick={saveDiscoveryKeys}
              disabled={savingDiscoveryKeys || (!apolloKey && !chKey)}
              className="bg-[#e6007e] hover:bg-[#c4006b] text-white"
              data-testid="outreach-save-discovery-keys-btn"
            >
              {savingDiscoveryKeys ? 'Saving…' : 'Save discovery keys'}
            </Button>
          </CardContent>
        </Card>

        {/* Sending Limits */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Clock className="w-5 h-5 mr-2 text-[#e6007e]" />
              Sending Limits & Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Daily Email Limit</Label>
                <Input
                  type="number"
                  value={settings.daily_email_limit || 50}
                  onChange={(e) => setSettings({...settings, daily_email_limit: parseInt(e.target.value)})}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
              <div>
                <Label className="text-gray-400">Timezone</Label>
                <Input
                  value={settings.timezone || "Europe/London"}
                  onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Send Window Start</Label>
                <Input
                  type="time"
                  value={settings.send_window_start || "09:00"}
                  onChange={(e) => setSettings({...settings, send_window_start: e.target.value})}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
              <div>
                <Label className="text-gray-400">Send Window End</Label>
                <Input
                  type="time"
                  value={settings.send_window_end || "17:00"}
                  onChange={(e) => setSettings({...settings, send_window_end: e.target.value})}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* UK SME Targeting Settings */}
        <Card className="bg-gray-800 border-gray-700 mb-6 border-[#e6007e]/50">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Target className="w-5 h-5 mr-2 text-[#e6007e]" />
              Lead Targeting (UK SMEs)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-[#e6007e]/10 border border-[#e6007e]/30 rounded-lg p-3 mb-4">
              <p className="text-[#e6007e] text-sm font-medium">Smart Targeting Enabled</p>
              <p className="text-gray-400 text-xs mt-1">
                Only decision makers with real names will be contacted. Generic emails (info@, contact@, etc.) are automatically filtered out.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Target Country</Label>
                <Select 
                  value={settings.target_country || "GB"}
                  onValueChange={(v) => setSettings({...settings, target_country: v})}
                >
                  <SelectTrigger className="bg-white text-gray-900 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="IE">Ireland</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400">Company Size</Label>
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    value={settings.target_company_size_min || 1}
                    onChange={(e) => setSettings({...settings, target_company_size_min: parseInt(e.target.value) || 1})}
                    className="bg-white text-gray-900 border-gray-300 w-20"
                    min="1"
                  />
                  <span className="text-gray-400 self-center">to</span>
                  <Input
                    type="number"
                    value={settings.target_company_size_max || 10}
                    onChange={(e) => setSettings({...settings, target_company_size_max: parseInt(e.target.value) || 10})}
                    className="bg-white text-gray-900 border-gray-300 w-20"
                    min="1"
                  />
                  <span className="text-gray-400 self-center text-sm">employees</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-gray-400">Target Seniority Levels</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {["executive", "senior", "founder", "owner", "director", "manager"].map((level) => (
                  <button
                    key={level}
                    onClick={() => {
                      const current = settings.target_seniority || ["executive", "senior", "founder", "owner"];
                      if (current.includes(level)) {
                        setSettings({...settings, target_seniority: current.filter(l => l !== level)});
                      } else {
                        setSettings({...settings, target_seniority: [...current, level]});
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      (settings.target_seniority || ["executive", "senior", "founder", "owner"]).includes(level)
                        ? 'bg-[#e6007e] text-white'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-2">Click to toggle. These are the job levels we'll target.</p>
            </div>

            <div className="flex items-center space-x-3 pt-2">
              <Checkbox
                id="require_named"
                checked={settings.require_named_contact !== false}
                onCheckedChange={(checked) => setSettings({...settings, require_named_contact: checked})}
              />
              <Label htmlFor="require_named" className="text-white cursor-pointer">
                Only contact named individuals (no generic emails like info@, contact@)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Follow-up Settings */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <RefreshCw className="w-5 h-5 mr-2 text-[#e6007e]" />
              Follow-up Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="auto_followup"
                checked={settings.auto_follow_up_enabled || false}
                onCheckedChange={(checked) => setSettings({...settings, auto_follow_up_enabled: checked})}
              />
              <Label htmlFor="auto_followup" className="text-white cursor-pointer">Enable automatic follow-ups</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Days between follow-ups</Label>
                <Input
                  type="number"
                  value={settings.follow_up_delay_days || 3}
                  onChange={(e) => setSettings({...settings, follow_up_delay_days: parseInt(e.target.value)})}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
              <div>
                <Label className="text-gray-400">Max follow-ups per lead</Label>
                <Input
                  type="number"
                  value={settings.max_follow_ups || 3}
                  onChange={(e) => setSettings({...settings, max_follow_ups: parseInt(e.target.value)})}
                  className="bg-white text-gray-900 border-gray-300"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};


// ==================== PIPELINE PAGE ====================

export const OutreachPipelinePage = () => {
  const [pipeline, setPipeline] = useState({ stats: {}, deals: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  const stages = [
    { key: 'new', label: 'New', color: 'bg-gray-500' },
    { key: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
    { key: 'replied', label: 'Replied', color: 'bg-purple-500' },
    { key: 'meeting_booked', label: 'Meeting', color: 'bg-yellow-500' },
    { key: 'proposal_sent', label: 'Proposal', color: 'bg-orange-500' },
    { key: 'converted', label: 'Won', color: 'bg-green-500' },
    { key: 'lost', label: 'Lost', color: 'bg-red-500' }
  ];

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchPipeline();
  }, [adminToken, navigate]);

  const fetchPipeline = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/outreach/pipeline`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPipeline(response.data);
    } catch (err) {
      console.error('Failed to fetch pipeline', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (dealId, newStage) => {
    try {
      await axios.put(`${API}/api/admin/outreach/pipeline/${dealId}/stage`, null, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { stage: newStage }
      });
      toast({ title: "Success", description: "Deal updated" });
      fetchPipeline();
    } catch (err) {
      toast({ title: "Error", description: "Failed to update deal", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/automation')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Zap className="w-4 h-4 inline mr-2" />Automation
            </button>
            <button onClick={() => navigate('/admin/outreach/pipeline')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap">
              <TrendingUp className="w-4 h-4 inline mr-2" />Pipeline
            </button>
            <button onClick={() => navigate('/admin/outreach/settings')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Settings className="w-4 h-4 inline mr-2" />Settings
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-2">Sales Pipeline</h1>
        <p className="text-gray-400 mb-8">Track deals from first contact to conversion</p>

        {/* Pipeline Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {stages.map((stage) => (
            <div key={stage.key} className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-center">
              <div className={`w-3 h-3 rounded-full ${stage.color} mx-auto mb-2`}></div>
              <p className="text-gray-400 text-xs">{stage.label}</p>
              <p className="text-2xl font-bold text-white">{pipeline.stats[stage.key]?.count || 0}</p>
              <p className="text-[#e6007e] text-sm">
                £{((pipeline.stats[stage.key]?.total_value || 0) / 100).toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* Deals List */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Active Deals</CardTitle>
          </CardHeader>
          <CardContent>
            {pipeline.deals.length > 0 ? (
              <div className="space-y-3">
                {pipeline.deals.map((deal) => (
                  <div key={deal.id} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{deal.title}</p>
                      <p className="text-gray-400 text-sm">£{(deal.value || 0).toLocaleString()}</p>
                    </div>
                    <Select value={deal.stage} onValueChange={(v) => handleStageChange(deal.id, v)}>
                      <SelectTrigger className="w-40 bg-gray-700 border-gray-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => (
                          <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No deals in pipeline yet</p>
                <p className="text-sm">Deals are created when leads are converted</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default OutreachDashboard;

// ==================== SECTOR PRESETS PAGE (editable) ====================

const _emptyPresetForm = () => ({
  slug: "",
  name: "",
  description: "",
  icon: "📨",
  tags: "",
  initial: { subject: "", body_text: "" },
  followup_1: { subject: "", body_text: "" },
  followup_2: { subject: "", body_text: "" },
});

export const OutreachSectorPresetsPage = () => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingSlug, setApplyingSlug] = useState(null);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // 'create' | 'edit'
  const [editorForm, setEditorForm] = useState(_emptyPresetForm());
  const [savingEditor, setSavingEditor] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const fetchPresets = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/sector-presets`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setPresets(r.data.presets || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Could not load sector presets', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (preset) => {
    setApplyingSlug(preset.slug);
    try {
      await axios.post(
        `${API}/api/admin/outreach/sector-presets/${preset.slug}/apply`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({ title: `${preset.name} loaded`, description: 'Templates created.' });
      setTimeout(() => navigate('/admin/outreach/templates'), 800);
    } catch (err) {
      toast({ title: 'Could not apply preset', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setApplyingSlug(null);
    }
  };

  const openCreate = () => {
    setEditorMode('create');
    setEditorForm(_emptyPresetForm());
    setEditorOpen(true);
  };

  const openEdit = async (slug) => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/sector-presets/${slug}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const p = r.data;
      setEditorMode('edit');
      setEditorForm({
        slug: p.slug,
        name: p.name || '',
        description: p.description || '',
        icon: p.icon || '📨',
        tags: (p.tags || []).join(', '),
        initial: { subject: p.initial?.subject || '', body_text: p.initial?.body_text || '' },
        followup_1: { subject: p.followup_1?.subject || '', body_text: p.followup_1?.body_text || '' },
        followup_2: { subject: p.followup_2?.subject || '', body_text: p.followup_2?.body_text || '' },
      });
      setEditorOpen(true);
    } catch (err) {
      toast({ title: 'Could not load preset', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleSaveEditor = async () => {
    setSavingEditor(true);
    try {
      const payload = {
        name: editorForm.name,
        description: editorForm.description,
        icon: editorForm.icon,
        tags: editorForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
        initial: editorForm.initial,
        followup_1: editorForm.followup_1,
        followup_2: editorForm.followup_2,
      };
      if (editorMode === 'create') {
        await axios.post(`${API}/api/admin/outreach/sector-presets`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        toast({ title: 'Sector created', description: `${payload.name} is ready to use.` });
      } else {
        await axios.put(`${API}/api/admin/outreach/sector-presets/${editorForm.slug}`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        toast({ title: 'Saved', description: `${payload.name} updated.` });
      }
      setEditorOpen(false);
      fetchPresets();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSavingEditor(false);
    }
  };

  const handleDelete = async (preset) => {
    if (!window.confirm(`Delete "${preset.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/outreach/sector-presets/${preset.slug}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast({ title: 'Deleted', description: `${preset.name} removed.` });
      fetchPresets();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleClone = async (preset) => {
    const newName = window.prompt('Name for the cloned sector:', `${preset.name} (copy)`);
    if (!newName) return;
    try {
      await axios.post(`${API}/api/admin/outreach/sector-presets/${preset.slug}/clone`, { name: newName }, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast({ title: 'Cloned', description: newName });
      fetchPresets();
    } catch (err) {
      toast({ title: 'Clone failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  const previewing = previewSlug ? presets.find((p) => p.slug === previewSlug) : null;

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Sector Presets</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 flex overflow-x-auto">
          <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><BarChart3 className="w-4 h-4 inline mr-2" /> Overview</button>
          <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Users className="w-4 h-4 inline mr-2" /> Leads</button>
          <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Send className="w-4 h-4 inline mr-2" /> Campaigns</button>
          <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><FileText className="w-4 h-4 inline mr-2" /> Templates</button>
          <button className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap"><Sparkles className="w-4 h-4 inline mr-2" /> Sector Presets</button>
          <button onClick={() => navigate('/admin/outreach/engine-settings')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Settings className="w-4 h-4 inline mr-2" /> Engine Settings</button>
          <button onClick={() => navigate('/admin/outreach/settings')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Mail className="w-4 h-4 inline mr-2" /> SMTP & DNS</button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Sector Presets</h1>
            <p className="text-gray-400 max-w-2xl">
              Pick a sector → 1-click creates a 3-email sequence (initial + 2 follow-ups). Add new leads to the same tag later — they'll automatically use the same flow.
            </p>
          </div>
          <Button onClick={openCreate} className="bg-[#e6007e] hover:bg-[#c4006b]" data-testid="add-sector-preset-btn">
            <Plus className="w-4 h-4 mr-2" /> Add sector
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {presets.map((preset) => (
            <Card key={preset.slug} className="bg-gray-800 border-gray-700 hover:border-[#e6007e]/40 transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{preset.icon}</span>
                    <h3 className="text-lg font-semibold text-white">{preset.name}</h3>
                  </div>
                  {preset.tags?.length > 0 && (
                    <div className="flex gap-1 flex-wrap justify-end">
                      {preset.tags.slice(0, 2).map((t) => (
                        <span key={t} className="text-xs bg-gray-700 text-gray-200 px-2 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-400 mb-4 min-h-[40px]">{preset.description}</p>
                <div className="bg-gray-900/40 border border-gray-700 rounded p-3 mb-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Subject</p>
                  <p className="text-sm text-white font-mono mb-2 truncate">{preset.preview.subject}</p>
                  <p className="text-xs text-gray-300">+ Day 3 + Day 7 follow-ups · auto-pause on reply</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setPreviewSlug(preset.slug)} className="text-gray-300 hover:text-white" data-testid={`preset-preview-${preset.slug}`}>Preview</Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(preset.slug)} className="text-blue-300 hover:text-blue-200" data-testid={`preset-edit-${preset.slug}`}>
                    <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleClone(preset)} className="text-gray-300 hover:text-white" data-testid={`preset-clone-${preset.slug}`}>Clone</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(preset)} className="text-red-400 hover:text-red-300 ml-auto" data-testid={`preset-delete-${preset.slug}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Button onClick={() => handleApply(preset)} disabled={applyingSlug === preset.slug} className="bg-[#e6007e] hover:bg-[#c4006b] w-full mt-2" data-testid={`preset-apply-${preset.slug}`}>
                  {applyingSlug === preset.slug ? 'Loading…' : `Use ${preset.name}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {/* Preview modal */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewSlug(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          {previewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="text-2xl">{previewing.icon}</span>
                  {previewing.name} — preview
                </DialogTitle>
                <DialogDescription className="text-gray-400">{previewing.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wide text-[#e6007e] mb-1">Initial · Day 0</p>
                  <p className="text-sm text-gray-300 mb-2"><strong>Subject:</strong> {previewing.preview.subject}</p>
                  <pre className="text-sm text-gray-200 whitespace-pre-wrap font-sans">{previewing.preview.body_text}</pre>
                </div>
                {previewing.follow_ups.map((fu, i) => (
                  <div key={i} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wide text-[#e6007e] mb-1">Follow-up {i + 1} · Day {fu.delay_days}</p>
                    <p className="text-sm text-gray-300"><strong>Subject:</strong> {fu.subject}</p>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPreviewSlug(null)}>Close</Button>
                <Button onClick={() => { handleApply(previewing); setPreviewSlug(null); }} className="bg-[#e6007e] hover:bg-[#c4006b]">Use this preset</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Editor modal */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editorMode === 'create' ? 'New sector preset' : `Edit ${editorForm.name}`}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Available tokens: <code className="text-[#e6007e]">{`{{first_name}}`}</code> · <code className="text-[#e6007e]">{`{{company_name}}`}</code> · <code className="text-[#e6007e]">{`{{industry}}`}</code>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Sector name</Label>
                <Input value={editorForm.name} onChange={(e) => setEditorForm({ ...editorForm, name: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" placeholder="e.g. Gardeners" data-testid="preset-form-name" />
              </div>
              <div>
                <Label className="text-gray-300">Icon (emoji)</Label>
                <Input value={editorForm.icon} onChange={(e) => setEditorForm({ ...editorForm, icon: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" placeholder="🌿" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Description</Label>
              <Input value={editorForm.description} onChange={(e) => setEditorForm({ ...editorForm, description: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" placeholder="Short blurb shown on the card" />
            </div>
            <div>
              <Label className="text-gray-300">Tags (comma-separated)</Label>
              <Input value={editorForm.tags} onChange={(e) => setEditorForm({ ...editorForm, tags: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" placeholder="gardeners, landscapers" />
            </div>

            {[
              { key: 'initial', label: 'Initial email · Day 0' },
              { key: 'followup_1', label: 'Follow-up 1 · Day 3' },
              { key: 'followup_2', label: 'Follow-up 2 · Day 7' },
            ].map(({ key, label }) => (
              <div key={key} className="bg-gray-900/40 border border-gray-700 rounded-lg p-4">
                <Label className="text-[#e6007e] text-xs uppercase tracking-wide">{label}</Label>
                <Input
                  value={editorForm[key].subject}
                  onChange={(e) => setEditorForm({ ...editorForm, [key]: { ...editorForm[key], subject: e.target.value } })}
                  className="bg-gray-700 border-gray-600 mt-2"
                  placeholder="Subject line"
                  data-testid={`preset-form-${key}-subject`}
                />
                <Textarea
                  value={editorForm[key].body_text}
                  onChange={(e) => setEditorForm({ ...editorForm, [key]: { ...editorForm[key], body_text: e.target.value } })}
                  className="bg-gray-700 border-gray-600 mt-2 min-h-[120px] font-mono text-sm"
                  placeholder="Body text… use blank line between paragraphs"
                  data-testid={`preset-form-${key}-body`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveEditor}
              disabled={savingEditor || !editorForm.name.trim()}
              className="bg-[#e6007e] hover:bg-[#c4006b]"
              data-testid="preset-form-save-btn"
            >
              {savingEditor ? 'Saving…' : (editorMode === 'create' ? 'Create sector' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ENGINE SETTINGS PAGE ====================

const _DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const OutreachEngineSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const fetchSettings = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/outreach/engine-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setSettings(r.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Could not load settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      // Cast curve keys: int → string would break Mongo, but the backend accepts either
      const payload = {
        send_window_enabled: !!settings.send_window_enabled,
        send_timezone: settings.send_timezone,
        send_days: settings.send_days,
        send_hour_start: parseInt(settings.send_hour_start, 10),
        send_hour_end: parseInt(settings.send_hour_end, 10),
        skip_uk_holidays: !!settings.skip_uk_holidays,
        warmup_enabled: !!settings.warmup_enabled,
        warmup_curve: Object.fromEntries(
          Object.entries(settings.warmup_curve || {}).map(([k, v]) => [k, parseInt(v, 10) || 0])
        ),
        daily_email_limit: parseInt(settings.daily_email_limit, 10) || 200,
      };
      const r = await axios.put(`${API}/api/admin/outreach/engine-settings`, payload, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setSettings(r.data);
      toast({ title: 'Saved', description: 'Engine settings updated.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (d) => {
    const days = new Set(settings.send_days || []);
    if (days.has(d)) days.delete(d); else days.add(d);
    setSettings({ ...settings, send_days: Array.from(days).sort() });
  };

  const updateCurveDay = (day, val) => {
    setSettings({
      ...settings,
      warmup_curve: { ...(settings.warmup_curve || {}), [day]: parseInt(val, 10) || 0 },
    });
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e]"></div>
      </div>
    );
  }

  const curveEntries = Object.entries(settings.warmup_curve || {})
    .map(([k, v]) => [parseInt(k, 10), v])
    .sort((a, b) => a[0] - b[0]);

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Engine Settings</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </header>

      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 flex overflow-x-auto">
          <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><BarChart3 className="w-4 h-4 inline mr-2" /> Overview</button>
          <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Users className="w-4 h-4 inline mr-2" /> Leads</button>
          <button onClick={() => navigate('/admin/outreach/campaigns')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Send className="w-4 h-4 inline mr-2" /> Campaigns</button>
          <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><FileText className="w-4 h-4 inline mr-2" /> Templates</button>
          <button onClick={() => navigate('/admin/outreach/sector-presets')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Sparkles className="w-4 h-4 inline mr-2" /> Sector Presets</button>
          <button className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap"><Settings className="w-4 h-4 inline mr-2" /> Engine Settings</button>
          <button onClick={() => navigate('/admin/outreach/settings')} className="px-4 py-3 text-gray-400 hover:text-white whitespace-nowrap"><Mail className="w-4 h-4 inline mr-2" /> SMTP & DNS</button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Engine Settings</h1>
          <p className="text-gray-400">Smart Send Window + SMTP Warm-Up curve. Changes take effect on the next scheduler tick (within 2 minutes).</p>
        </div>

        {/* Send window */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Clock className="w-5 h-5 text-[#e6007e]" /> Smart Send Window</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={!!settings.send_window_enabled}
                onChange={(e) => setSettings({ ...settings, send_window_enabled: e.target.checked })}
                className="w-4 h-4"
                data-testid="settings-window-enabled"
              />
              <span className="text-white">Restrict outbound sends to a window (recommended)</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">Timezone</Label>
                <Input value={settings.send_timezone} onChange={(e) => setSettings({ ...settings, send_timezone: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Start hour (0-23)</Label>
                <Input type="number" min="0" max="23" value={settings.send_hour_start} onChange={(e) => setSettings({ ...settings, send_hour_start: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" data-testid="settings-hour-start" />
              </div>
              <div>
                <Label className="text-gray-300">End hour (1-24)</Label>
                <Input type="number" min="1" max="24" value={settings.send_hour_end} onChange={(e) => setSettings({ ...settings, send_hour_end: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" data-testid="settings-hour-end" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Send days</Label>
              <div className="flex flex-wrap gap-2">
                {_DAY_LABELS.map((label, idx) => {
                  const active = (settings.send_days || []).includes(idx);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      className={`px-3 py-1.5 rounded-md text-sm transition ${active ? 'bg-[#e6007e] text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                      data-testid={`settings-day-${label.toLowerCase()}`}
                    >{label}</button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={!!settings.skip_uk_holidays}
                onChange={(e) => setSettings({ ...settings, skip_uk_holidays: e.target.checked })}
                className="w-4 h-4"
                data-testid="settings-skip-holidays"
              />
              <span className="text-white">Skip UK bank holidays (England & Wales)</span>
            </label>
          </CardContent>
        </Card>

        {/* Warm-up */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#e6007e]" /> SMTP Warm-Up Curve</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={!!settings.warmup_enabled}
                onChange={(e) => setSettings({ ...settings, warmup_enabled: e.target.checked })}
                className="w-4 h-4"
                data-testid="settings-warmup-enabled"
              />
              <span className="text-white">Use warm-up ramp for new SMTP setups</span>
            </label>
            <div>
              <Label className="text-gray-300 mb-2 block">Daily cap per warm-up day</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                {curveEntries.map(([day, cap]) => (
                  <div key={day} className="bg-gray-900/40 border border-gray-700 rounded p-2">
                    <p className="text-xs text-gray-400 mb-1">Day {day}</p>
                    <Input type="number" min="0" value={cap} onChange={(e) => updateCurveDay(day, e.target.value)} className="bg-gray-700 border-gray-600 h-8 text-sm" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">After day {curveEntries.length}, sending uses the configured daily limit below.</p>
            </div>
            <div>
              <Label className="text-gray-300">Daily email limit (post warm-up)</Label>
              <Input type="number" min="1" value={settings.daily_email_limit} onChange={(e) => setSettings({ ...settings, daily_email_limit: e.target.value })} className="bg-gray-700 border-gray-600 mt-1 max-w-xs" data-testid="settings-daily-limit" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-[#e6007e] hover:bg-[#c4006b]" data-testid="settings-save-btn">
            {saving ? 'Saving…' : 'Save engine settings'}
          </Button>
        </div>
      </main>
    </div>
  );
};


// ==================== LISTS PAGE (simplified dead-simple flow) ====================

export const OutreachListsPage = () => {
  const [lists, setLists] = useState([]);
  const [untagged, setUntagged] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendDialog, setSendDialog] = useState({ open: false, tag: null, count: 0 });
  const [sendForm, setSendForm] = useState({ template_id: '', campaign_name: '' });
  const [sending, setSending] = useState(false);
  const [addDialog, setAddDialog] = useState({ open: false, tag: null });
  const [addForm, setAddForm] = useState({ email: '', full_name: '', company_name: '' });
  const [adding, setAdding] = useState(false);
  const [presetSending, setPresetSending] = useState(null); // tag currently sending preset
  const [lastSend, setLastSend] = useState(null); // {tag, sent, queued, failed, at, type}
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const adminToken = sessionStorage.getItem('admin_token');
  const adminUser = JSON.parse(sessionStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    fetchLists();
    fetchTemplates();
    fetchHistory();
  }, [adminToken, navigate]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/outreach/lists/send-history?limit=5`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setHistory(res.data.history || []);
    } catch {
      // non-critical
    }
  };

  const fetchLists = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/outreach/lists`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setLists(res.data.lists || []);
      setUntagged(res.data.untagged || 0);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to load lists', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/outreach/templates`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setTemplates(res.data.templates || []);
    } catch {
      // non-critical
    }
  };

  const openSend = (tag, count) => {
    setSendForm({ template_id: '', campaign_name: '' });
    setSendDialog({ open: true, tag, count });
  };

  const handleSend = async () => {
    if (!sendForm.template_id) {
      toast({ title: 'Choose a template', description: 'Pick the email you want to send.', variant: 'destructive' });
      return;
    }
    setSending(true);
    const tag = sendDialog.tag;
    try {
      const res = await axios.post(
        `${API}/api/admin/outreach/lists/${encodeURIComponent(tag)}/send`,
        {
          template_id: sendForm.template_id,
          campaign_name: sendForm.campaign_name || undefined,
          send_now: true,
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const d = res.data || {};
      setLastSend({
        tag,
        sent: d.sent_now || 0,
        queued: d.queued || 0,
        failed: d.sent_failed || 0,
        at: new Date(),
        type: 'custom',
      });
      toast({
        title: `${d.sent_now || 0} sent to "${tag}"`,
        description: d.message,
        duration: 6000,
      });
      setSendDialog({ open: false, tag: null, count: 0 });
      fetchLists();
      fetchHistory();
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleProcessNow = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/outreach/process-queue`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast({ title: 'Queue processed', description: `${res.data.sent || 0} sent, ${res.data.failed || 0} failed.` });
    } catch (err) {
      toast({ title: 'Process failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleSendPreset = async (list) => {
    if (!list.preset) return;
    const ok = window.confirm(
      `Send the "${list.preset.name}" preset to ${list.count} lead${list.count !== 1 ? 's' : ''} in "${list.tag}" now?\n\nFires immediately, bypassing the send window. Suppressed/unsubscribed/bounced are skipped.`
    );
    if (!ok) return;
    setPresetSending(list.tag);
    try {
      const res = await axios.post(
        `${API}/api/admin/outreach/lists/${encodeURIComponent(list.tag)}/send-preset`,
        { step: 'initial', send_now: true },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const d = res.data || {};
      setLastSend({
        tag: list.tag,
        preset_name: d.preset_name,
        sent: d.sent_now || 0,
        queued: d.queued || 0,
        failed: d.sent_failed || 0,
        at: new Date(),
        type: 'preset',
      });
      toast({
        title: `${d.sent_now || 0} sent to "${list.tag}"`,
        description: `Preset: ${d.preset_name}. ${d.message || ''}`,
        duration: 6000,
      });
      fetchLists();
      fetchHistory();
    } catch (err) {
      toast({ title: 'Preset send failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setPresetSending(null);
    }
  };

  const openAdd = (tag) => {
    setAddForm({ email: '', full_name: '', company_name: '' });
    setAddDialog({ open: true, tag });
  };

  const handleAdd = async () => {
    if (!addForm.email) return;
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/outreach/lists/${encodeURIComponent(addDialog.tag)}/quick-add`,
        addForm,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast({
        title: res.data.created ? 'Lead added' : 'Lead tagged',
        description: `${addForm.email} is now in "${addDialog.tag}".`,
      });
      setAddDialog({ open: false, tag: null });
      fetchLists();
    } catch (err) {
      toast({ title: 'Add failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const totalLeads = lists.reduce((s, l) => s + (l.count || 0), 0) + untagged;

  return (
    <div className="min-h-screen bg-gray-900" data-testid="outreach-lists-page">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/paify-logo.png" alt="Paify" className="h-8" />
            <span className="text-white font-semibold">Admin Portal</span>
            <span className="text-[#e6007e] text-xs px-2 py-1 bg-[#e6007e]/10 rounded">Outreach Module</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">{adminUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4 overflow-x-auto">
            <button onClick={() => navigate('/admin/outreach')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Overview
            </button>
            <button onClick={() => navigate('/admin/outreach/lists')} className="px-4 py-3 text-white border-b-2 border-[#e6007e] whitespace-nowrap" data-testid="lists-nav">
              <ListIcon className="w-4 h-4 inline mr-2" />Lists
            </button>
            <button onClick={() => navigate('/admin/outreach/leads')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Leads
            </button>
            <button onClick={() => navigate('/admin/outreach/templates')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <FileText className="w-4 h-4 inline mr-2" />Templates
            </button>
            <button onClick={() => navigate('/admin/outreach/sector-presets')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Sparkles className="w-4 h-4 inline mr-2" />Sector Presets
            </button>
            <button onClick={() => navigate('/admin/outreach/engine-settings')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Settings className="w-4 h-4 inline mr-2" />Engine Settings
            </button>
            <button onClick={() => navigate('/admin/outreach/settings')} className="px-4 py-3 text-gray-400 hover:text-white border-b-2 border-transparent whitespace-nowrap">
              <Mail className="w-4 h-4 inline mr-2" />SMTP & DNS
            </button>
            <button onClick={() => navigate('/admin/dashboard')} className="px-4 py-3 text-gray-500 hover:text-gray-400 border-b-2 border-transparent whitespace-nowrap">
              ← Back to Main Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Lists</h1>
            <p className="text-gray-400 text-sm">
              Upload a CSV with a category tag (e.g. <span className="text-white">plumbers</span>), then click <span className="text-white">Send</span>. That's it.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleProcessNow}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              data-testid="lists-process-now-btn"
            >
              <Zap className="w-4 h-4 mr-2" />Process queue now
            </Button>
            <Button
              onClick={() => navigate('/admin/outreach/leads')}
              className="bg-[#e6007e] hover:bg-[#c4006b]"
              data-testid="lists-import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />Import CSV → Tag
            </Button>
          </div>
        </div>

        {/* Last Send Banner — persistent confirmation of the most recent send */}
        {lastSend && (
          <div
            className={`mb-6 rounded-xl border px-5 py-4 flex items-start gap-4 ${
              (lastSend.sent || 0) > 0
                ? 'bg-emerald-500/10 border-emerald-500/40'
                : 'bg-yellow-500/10 border-yellow-500/40'
            }`}
            data-testid="lists-last-send-banner"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              (lastSend.sent || 0) > 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-yellow-500/20 text-yellow-300'
            }`}>
              {(lastSend.sent || 0) > 0 ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold">
                {(lastSend.sent || 0) > 0
                  ? `${lastSend.sent} email${lastSend.sent !== 1 ? 's' : ''} sent to "${lastSend.tag}"`
                  : `${lastSend.queued} queued, none sent yet`}
                {lastSend.preset_name && <span className="ml-2 text-xs font-normal text-emerald-300">· {lastSend.preset_name} preset</span>}
              </p>
              <p className="text-xs text-gray-300 mt-0.5">
                {lastSend.queued} total · {lastSend.sent || 0} sent · {lastSend.failed || 0} failed
                {' · '}{new Date(lastSend.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                {(lastSend.queued - (lastSend.sent || 0) - (lastSend.failed || 0)) > 0 && (
                  <span className="text-yellow-300"> · {lastSend.queued - (lastSend.sent || 0) - (lastSend.failed || 0)} still queued (warm-up cap)</span>
                )}
              </p>
            </div>
            <button
              onClick={() => setLastSend(null)}
              className="text-gray-400 hover:text-white shrink-0"
              data-testid="lists-last-send-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Lists</p>
              <p className="text-2xl font-bold text-white" data-testid="lists-count">{lists.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Total leads</p>
              <p className="text-2xl font-bold text-white">{totalLeads}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Untagged</p>
              <p className="text-2xl font-bold text-yellow-400">{untagged}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-4 pb-4">
              <p className="text-gray-400 text-xs">Templates ready</p>
              <p className="text-2xl font-bold text-white">{templates.length}</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#e6007e]"></div>
          </div>
        ) : lists.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="py-16 text-center">
              <Inbox className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">No lists yet</h3>
              <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
                Import a CSV of leads with a tag (e.g. <span className="text-white">plumbers</span>, <span className="text-white">electricians</span>). Each tag becomes a list you can send to in one click.
              </p>
              <Button
                onClick={() => navigate('/admin/outreach/leads')}
                className="bg-[#e6007e] hover:bg-[#c4006b]"
                data-testid="lists-empty-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />Import your first list
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lists.map((l) => (
              <Card key={l.tag} className="bg-gray-800 border-gray-700 hover:border-[#e6007e]/40 transition-colors flex flex-col" data-testid={`list-card-${l.tag}`}>
                <CardContent className="pt-5 pb-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <h3 className="text-white font-semibold text-lg truncate capitalize">{l.name}</h3>
                      <p className="text-gray-400 text-xs mt-0.5">
                        {l.last_added ? `Last added ${new Date(l.last_added).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : 'No activity yet'}
                      </p>
                      {l.preset && (
                        <p className="text-xs text-emerald-300 mt-1.5 flex items-center gap-1" data-testid={`list-preset-badge-${l.tag}`}>
                          <Sparkles className="w-3 h-3" />
                          <span>Preset matched: <span className="font-medium">{l.preset.name}</span></span>
                        </p>
                      )}
                    </div>
                    <span className="bg-[#e6007e]/10 text-[#e6007e] text-sm font-bold px-2.5 py-1 rounded shrink-0" data-testid={`list-count-${l.tag}`}>
                      {l.count}
                    </span>
                  </div>

                  <div className="mt-auto pt-4 space-y-2">
                    {l.preset ? (
                      <Button
                        onClick={() => handleSendPreset(l)}
                        disabled={presetSending === l.tag}
                        className="w-full bg-gradient-to-r from-[#e6007e] to-[#ff4da6] hover:opacity-90 h-11"
                        data-testid={`list-send-preset-btn-${l.tag}`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {presetSending === l.tag
                          ? `Sending to ${l.count}…`
                          : `Send ${l.preset.name} preset to ${l.count}`}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openSend(l.tag, l.count)}
                        className="w-full bg-[#e6007e] hover:bg-[#c4006b] h-11"
                        data-testid={`list-send-btn-${l.tag}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {`Send to ${l.count} lead${l.count !== 1 ? 's' : ''} now`}
                      </Button>
                    )}
                    <div className="flex gap-2">
                      {l.preset && (
                        <Button
                          onClick={() => openSend(l.tag, l.count)}
                          variant="outline"
                          className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-700 text-xs h-9"
                          data-testid={`list-custom-send-btn-${l.tag}`}
                          title="Pick a different template"
                        >
                          <Send className="w-3 h-3 mr-1.5" />Custom send
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => openAdd(l.tag)}
                        className={`${l.preset ? '' : 'flex-1 '}border-gray-700 text-gray-300 hover:bg-gray-700 h-9`}
                        data-testid={`list-add-lead-btn-${l.tag}`}
                        title="Quick add a lead to this list"
                      >
                        <Plus className="w-4 h-4" />{!l.preset && <span className="ml-1.5 text-xs">Add lead</span>}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/admin/outreach/leads?tag=${encodeURIComponent(l.tag)}`)}
                        className={`${l.preset ? '' : 'flex-1 '}border-gray-700 text-gray-300 hover:bg-gray-700 h-9`}
                        data-testid={`list-view-leads-btn-${l.tag}`}
                        title="View leads in this list"
                      >
                        <Eye className="w-4 h-4" />{!l.preset && <span className="ml-1.5 text-xs">View</span>}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {untagged > 0 && (
              <Card className="bg-gray-800/50 border-gray-700 border-dashed flex flex-col">
                <CardContent className="pt-5 pb-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-gray-300 font-semibold text-lg">Untagged</h3>
                      <p className="text-gray-500 text-xs mt-0.5">Leads without a list tag</p>
                    </div>
                    <span className="bg-yellow-500/10 text-yellow-400 text-sm font-bold px-2.5 py-1 rounded">
                      {untagged}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/outreach/leads')}
                    className="mt-auto border-gray-700 text-gray-300 hover:bg-gray-700"
                  >
                    Tag these leads
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        {/* Send history feed */}
        {history.length > 0 && (
          <div className="mt-10" data-testid="lists-send-history">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                Send history
              </h3>
              <span className="text-xs text-gray-500">last {history.length} send{history.length !== 1 ? 's' : ''}</span>
            </div>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="py-3 px-4 divide-y divide-gray-700">
                {history.map((h) => {
                  const sent = h.sent || 0;
                  const failed = h.failed || 0;
                  const queued = h.queued || 0;
                  const total = h.total || 0;
                  const stateColor = sent > 0 ? 'text-emerald-300' : (failed > 0 ? 'text-red-300' : 'text-yellow-300');
                  const stateLabel = sent > 0
                    ? `${sent} sent`
                    : (queued > 0 ? `${queued} queued` : (failed > 0 ? `${failed} failed` : `${total} total`));
                  return (
                    <div key={h.campaign_id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-gray-400 font-mono text-xs shrink-0 w-12">
                          {h.created_at ? new Date(h.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                        <span className={`shrink-0 font-semibold ${stateColor}`}>{stateLabel}</span>
                        {h.tag && (
                          <span className="text-gray-300 truncate">
                            to <span className="capitalize text-white">{h.tag}</span>
                          </span>
                        )}
                        {h.preset_name && (
                          <span className="text-emerald-300/80 text-xs flex items-center gap-1 shrink-0">
                            <Sparkles className="w-3 h-3" />{h.preset_name} preset
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 shrink-0">
                        {total} total · {sent} sent · {queued} queued · {failed} failed
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      <Dialog open={sendDialog.open} onOpenChange={(o) => setSendDialog({ ...sendDialog, open: o })}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="lists-send-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">Send to "{sendDialog.tag}"</DialogTitle>
            <DialogDescription className="text-gray-400">
              {sendDialog.count} lead{sendDialog.count !== 1 ? 's' : ''} will receive this email immediately. Unsubscribed/bounced/suppressed are skipped automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-gray-300">Email template *</Label>
              <Select value={sendForm.template_id} onValueChange={(v) => setSendForm({ ...sendForm, template_id: v })}>
                <SelectTrigger className="bg-gray-700 border-gray-600 mt-1" data-testid="lists-send-template-select">
                  <SelectValue placeholder={templates.length === 0 ? 'No templates yet — create one first' : 'Pick a template'} />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-xs text-yellow-400 mt-1">
                  <button type="button" onClick={() => navigate('/admin/outreach/templates')} className="underline">Create a template →</button>
                </p>
              )}
            </div>

            <div>
              <Label className="text-gray-300">Campaign name (optional)</Label>
              <Input
                value={sendForm.campaign_name}
                onChange={(e) => setSendForm({ ...sendForm, campaign_name: e.target.value })}
                placeholder={`${sendDialog.tag} — ${new Date().toLocaleDateString('en-GB')}`}
                className="bg-gray-700 border-gray-600 mt-1"
                data-testid="lists-send-campaign-name"
              />
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 text-xs text-emerald-200">
              <Zap className="w-4 h-4 inline mr-2 -mt-0.5" />
              Sends immediately, bypassing the Mon-Fri send window. Warm-up daily caps still apply.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog({ open: false, tag: null, count: 0 })} className="border-gray-600 text-gray-300 hover:bg-gray-700">Cancel</Button>
            <Button onClick={handleSend} disabled={sending || !sendForm.template_id} className="bg-[#e6007e] hover:bg-[#c4006b]" data-testid="lists-send-confirm-btn">
              <Send className="w-4 h-4 mr-2" />
              {sending ? `Sending to ${sendDialog.count}…` : `Send to ${sendDialog.count} now`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(o) => setAddDialog({ ...addDialog, open: o })}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white" data-testid="lists-add-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">Add lead to "{addDialog.tag}"</DialogTitle>
            <DialogDescription className="text-gray-400">
              If the email already exists, we'll just tag it with this list.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-gray-300">Email *</Label>
              <Input
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                placeholder="contact@business.co.uk"
                className="bg-gray-700 border-gray-600 mt-1"
                data-testid="lists-add-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Full name</Label>
                <Input value={addForm.full_name} onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Company</Label>
                <Input value={addForm.company_name} onChange={(e) => setAddForm({ ...addForm, company_name: e.target.value })} className="bg-gray-700 border-gray-600 mt-1" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ open: false, tag: null })} className="border-gray-600 text-gray-300 hover:bg-gray-700">Cancel</Button>
            <Button onClick={handleAdd} disabled={adding || !addForm.email} className="bg-[#e6007e] hover:bg-[#c4006b]" data-testid="lists-add-confirm-btn">
              {adding ? 'Adding…' : 'Add to list'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
